import { computed, effect, inject, Injectable, Injector, isDevMode, signal, untracked } from '@angular/core';
import { TwitchChatStateService, SavedState, AnalysisCacheData } from './twitch-chat-state.service.js';
import { GeminiService, HighlightDeepAnalysisResponse, HighlightSearchResponse } from './gemini.service.js';
import { ActivityBin, ChatFilters, Highlight, TwitchChatLog, TwitchComment } from '../models/twitch.model';
import { FirebaseService } from './firebase.service.js';
import { LibraryService } from './library.service.js';
import { MarkersService } from './markers.service.js';
import { SyncService } from './sync.service.js';
import { TwitchApiService } from './twitch-api.service.js';
import { checkDemoMode } from './demo.service.js';
import { QuotaExceededError } from './quota.service.js';
import { highlightMatchSegments, matchText, parseSearchQuery } from '../utils/search-query.js';
import { isTranscriptSource } from '../models/transcript.model.js';
import { TimeRegion, binsInRegion, overlapsRegion, rebaseBins } from '../utils/region.js';
import { VodClip, capClipsByViews, mergeUserClips, userClipKey, vodClipFromGql } from '../utils/user-clips.js';
import { VodChapter, chaptersFromGqlVideo } from '../utils/vod-chapters.js';

interface PeakCluster {
  startTime: number;
  endTime: number;
  messageCount: number;
  messages: string[];
}

interface SearchSample {
  text: string;
  matchedKeywords: string[];
}

interface SearchBinAccum {
  matchCount: number;
  totalMessages: number;
  samples: SearchSample[];
}

interface SearchCluster {
  startTime: number;
  endTime: number;
  matchCount: number;
  totalMessages: number;
  samples: SearchSample[];
}

// --- Client-side Twitch GQL Chat Download ---
const TWITCH_GQL_URL = 'https://gql.twitch.tv/gql';
const TWITCH_GQL_CLIENT_ID = 'kd1unb4b3q4t58fwlpcbzcbnm76a8fp';
// Page-size hint. Twitch GQL caps `first` between 1 and 100 (verified 2026-05-09 via
// debug-parallel-chat.mjs: `first: 200` returned `argument 'first' value must be between
// 1 and 100`). 100 is the sweet spot — 2× larger than the empirical default (~50) and at
// the documented upper bound. `firstSupported` is the safety net: if Twitch ever changes
// or removes the arg, the GQL error path flips it off + retries without `first`.
const TWITCH_GQL_PAGE_SIZE_HINT = 100;
let firstSupported = true;

// Stable triple-key for comment dedup: (offset, commenter, body). Used by both the
// parallel-window orchestrator's boundary merge AND the live-poll tail dedup.
function commentDedupKey(c: TwitchComment): string {
  return `${c.content_offset_seconds}|${c.commenter?.display_name || ''}|${c.message?.body || ''}`;
}

async function gqlFetchVideoInfo(videoId: string) {
  const resp = await fetch(TWITCH_GQL_URL, {
    method: 'POST',
    headers: { 'Client-ID': TWITCH_GQL_CLIENT_ID, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: `query { video(id: "${videoId}") { id title createdAt lengthSeconds previewThumbnailURL(width: 640, height: 360) owner { displayName login } game { name } } }`
    })
  });
  const json = await resp.json();
  if (json.errors) throw new Error(json.errors[0]?.message || 'GQL error');
  return json.data.video;
}

async function gqlFetchCommentPage(videoId: string, cursor: string | null, contentOffsetSeconds: number) {
  const useFirst = firstSupported;
  const query = useFirst
    ? `query($videoID: ID!, $cursor: Cursor, $contentOffsetSeconds: Int, $first: Int) {
        video(id: $videoID) {
          comments(after: $cursor, contentOffsetSeconds: $contentOffsetSeconds, first: $first) {
            edges { cursor node { id commenter { displayName login } contentOffsetSeconds message { fragments { text } } } }
            pageInfo { hasNextPage }
          }
        }
      }`
    : `query($videoID: ID!, $cursor: Cursor, $contentOffsetSeconds: Int) {
        video(id: $videoID) {
          comments(after: $cursor, contentOffsetSeconds: $contentOffsetSeconds) {
            edges { cursor node { id commenter { displayName login } contentOffsetSeconds message { fragments { text } } } }
            pageInfo { hasNextPage }
          }
        }
      }`;
  const variables: any = { videoID: videoId };
  if (cursor) { variables.cursor = cursor; } else { variables.contentOffsetSeconds = contentOffsetSeconds; }
  if (useFirst) variables.first = TWITCH_GQL_PAGE_SIZE_HINT;

  const resp = await fetch(TWITCH_GQL_URL, {
    method: 'POST',
    headers: { 'Client-ID': TWITCH_GQL_CLIENT_ID, 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables })
  });
  const json = await resp.json();
  if (json.errors) {
    const msg = json.errors[0]?.message || 'GQL error';
    if (useFirst && /first/i.test(msg)) {
      firstSupported = false;
      return gqlFetchCommentPage(videoId, cursor, contentOffsetSeconds);
    }
    throw new Error(msg);
  }
  return json.data?.video?.comments;
}

// Every clip cut from this VOD, including ones clipped from the VOD after the stream —
// see fetchAndMergeUserClips for why this replaced Helix as the primary source. Returns
// null when the VOD itself is gone (deleted / expired) so saved clips stay untouched.
// Per page: 3 attempts, 1s / 2s apart. 30 pages × 100 = far above any real VOD.
async function gqlFetchVodClips(videoId: string): Promise<VodClip[] | null> {
  const query = `query($videoID: ID!, $after: Cursor, $first: Int) {
    video(id: $videoID) {
      clips(first: $first, after: $after) {
        edges { cursor node { slug url title viewCount duration videoOffsetSeconds curator { displayName } } }
        pageInfo { hasNextPage }
      }
    }
  }`;
  const clips: VodClip[] = [];
  let after: string | null = null;
  for (let page = 0; page < 30; page++) {
    let conn: any;
    for (let attempt = 1; ; attempt++) {
      try {
        const resp = await fetch(TWITCH_GQL_URL, {
          method: 'POST',
          headers: { 'Client-ID': TWITCH_GQL_CLIENT_ID, 'Content-Type': 'application/json' },
          body: JSON.stringify({ query, variables: { videoID: videoId, after, first: TWITCH_GQL_PAGE_SIZE_HINT } }),
        });
        const json = await resp.json();
        if (json.errors) throw new Error(json.errors[0]?.message || 'GQL error');
        if (!json.data?.video) return null;
        conn = json.data.video.clips;
        break;
      } catch (e) {
        if (attempt >= 3) throw e;
        await new Promise(r => setTimeout(r, 1000 * attempt));
      }
    }
    const edges: any[] = conn?.edges || [];
    for (const edge of edges) {
      const clip = vodClipFromGql(edge.node);
      if (clip) clips.push(clip);
    }
    after = conn?.pageInfo?.hasNextPage ? edges[edges.length - 1]?.cursor ?? null : null;
    if (!after) break;
  }
  return capClipsByViews(clips);
}

// Categories of the VOD — the chapters of the Twitch player (GAME_CHANGE moments). A VOD
// that stayed in one category has none, then it's `video.game` (utils/vod-chapters.ts).
// Returns null when the VOD itself is gone.
async function gqlFetchVodChapters(videoId: string): Promise<VodChapter[] | null> {
  const query = `query($videoID: ID!) {
    video(id: $videoID) {
      lengthSeconds
      game { id name displayName }
      moments(first: 100, momentRequestType: VIDEO_CHAPTER_MARKERS) {
        edges { node { type positionMilliseconds durationMilliseconds description details { ... on GameChangeMomentDetails { game { id name displayName } } } } }
      }
    }
  }`;
  const resp = await fetch(TWITCH_GQL_URL, {
    method: 'POST',
    headers: { 'Client-ID': TWITCH_GQL_CLIENT_ID, 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables: { videoID: videoId } }),
  });
  const json = await resp.json();
  if (json.errors) throw new Error(json.errors[0]?.message || 'GQL error');
  if (!json.data?.video) return null;
  return chaptersFromGqlVideo(json.data.video);
}

function formatGqlDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${h}h${m}m${s}s`;
}

interface ChatDownloadProgress {
  count: number;       // total comments downloaded so far
  currentSec: number;  // contentOffsetSeconds of the most recent comment
  totalSec: number;    // VOD lengthSeconds — 0 if unknown (rare)
}

// Parse a single GQL edge → TwitchComment (shared by sequential + parallel paths).
function edgeToComment(edge: any): TwitchComment {
  const node = edge.node;
  return {
    commenter: { display_name: node.commenter?.displayName || 'unknown' },
    message: { body: (node.message?.fragments || []).map((f: any) => f.text || '').join('') },
    content_offset_seconds: node.contentOffsetSeconds,
  };
}

// Single cursor-driven worker bounded to [startSec, endSec). endSec=Infinity means
// "until hasNextPage=false" (used for last parallel window AND fully-sequential mode).
// First page goes via contentOffsetSeconds=startSec (cursor=null); subsequent pages
// chain via cursor. Stops when lastOffset crosses endSec OR hasNextPage=false.
//
// Retry: linear backoff 1s × errorCount + 0..200ms jitter to desync workers under a
// shared 429 storm. Counter decays 0.1 per success. Throws after 10 consecutive errors.
async function runWindowLoop(
  videoId: string,
  startSec: number,
  endSec: number,
  startCursor: string | null,
  shouldContinue: () => boolean,
  onPage: (batch: TwitchComment[], lastOffset: number, nextCursor: string | null) => void,
): Promise<void> {
  let cursor: string | null = startCursor;
  let lastOffset = startSec;
  let errorCount = 0;
  // First-page sentinel: we use startSec for the offset arg only on the very first
  // request of this loop. Once cursor lands we never look at startSec again.
  let firstCall = cursor === null;

  while (shouldContinue()) {
    try {
      const offsetArg = firstCall ? startSec : 0;
      const page = await gqlFetchCommentPage(videoId, cursor, offsetArg);
      firstCall = false;
      if (!page?.edges?.length) break;

      const batch: TwitchComment[] = [];
      for (const edge of page.edges) {
        const c = edgeToComment(edge);
        batch.push(c);
        if (typeof c.content_offset_seconds === 'number') lastOffset = c.content_offset_seconds;
      }

      const nextCursor: string | null = page.pageInfo?.hasNextPage
        ? page.edges[page.edges.length - 1].cursor as string
        : null;

      onPage(batch, lastOffset, nextCursor);

      errorCount = Math.max(0, errorCount - 0.1);

      // Bounded window: stop once we've crossed the upper edge. Last batch may overhang
      // by a fraction of a second — final dedup pass cleans up overlaps with the next
      // window's first page (Twitch returns Int contentOffsetSeconds, so boundary-second
      // comments can land in both windows).
      if (Number.isFinite(endSec) && lastOffset >= endSec) break;
      if (!page.pageInfo?.hasNextPage) break;
      cursor = nextCursor;
    } catch (err: any) {
      errorCount++;
      if (errorCount > 10) throw err;
      const jitter = Math.floor(Math.random() * 200);
      await new Promise(r => setTimeout(r, 1000 * Math.ceil(errorCount) + jitter));
    }
  }
}

// k-way merge of per-window arrays into a globally sorted array. Each input is
// monotonically ordered by content_offset_seconds (Twitch cursor guarantee within
// a single chain). N is small (2..8) so a linear scan of N heads beats heap overhead.
function kWayMergeByOffset(arrays: TwitchComment[][]): TwitchComment[] {
  const totalLen = arrays.reduce((s, a) => s + a.length, 0);
  const out: TwitchComment[] = new Array(totalLen);
  const indices = arrays.map(() => 0);
  let writeIdx = 0;

  while (writeIdx < totalLen) {
    let minK = -1;
    let minOffset = Infinity;
    for (let k = 0; k < arrays.length; k++) {
      const i = indices[k];
      if (i < arrays[k].length) {
        const off = arrays[k][i].content_offset_seconds || 0;
        if (off < minOffset) { minOffset = off; minK = k; }
      }
    }
    if (minK === -1) break;
    out[writeIdx++] = arrays[minK][indices[minK]++];
  }
  out.length = writeIdx;
  return out;
}

function dedupCommentsByKey(arr: TwitchComment[]): TwitchComment[] {
  const seen = new Set<string>();
  const out: TwitchComment[] = [];
  for (const c of arr) {
    const k = commentDedupKey(c);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(c);
  }
  return out;
}

async function downloadChatClientSide(
  videoId: string,
  onProgress?: (info: ChatDownloadProgress) => void,
  onMeta?: (videoInfo: any) => void,
  onBatch?: (newComments: TwitchComment[], cursor: string | null) => void,
  startCursor: string | null = null,
  startOffset: number = 0,
) {
  const videoInfo = await gqlFetchVideoInfo(videoId);
  if (!videoInfo) throw new Error(`Video ${videoId} not found`);
  // Emit meta as soon as it arrives so callers can flip UI to "processed" before the
  // full chat download completes. Player iframe + sidebar shell can render immediately.
  if (onMeta) onMeta(videoInfo);
  const totalSec = videoInfo.lengthSeconds || 0;

  const videoMeta = {
    streamer: { name: videoInfo.owner.displayName, login: videoInfo.owner.login },
    video: {
      id: videoInfo.id, title: videoInfo.title, game: videoInfo.game?.name || '',
      created_at: videoInfo.createdAt, duration: formatGqlDuration(videoInfo.lengthSeconds),
      thumbnail_url: videoInfo.previewThumbnailURL || '',
      user_login: videoInfo.owner.login,
    }
  };

  // Sequential branch covers three caller intents:
  //   - Cursor-based resume (startCursor !== null) — auto-resume after tab-close mid-stream.
  //   - Offset-based continuation (startOffset > 0) — live-poll's per-tick resume from
  //     the last-known offset, also fallback resume when cursor was never persisted.
  //   - Unknown duration (totalSec <= 0) — Helix metadata couldn't size the windows;
  //     better to slip back to the prior single-cursor path than to make up windows.
  // In all three the caller wants "continue from this point", not "fan out across the VOD".
  const isSequential = startCursor !== null || startOffset > 0 || totalSec <= 0;

  if (isSequential) {
    const comments: TwitchComment[] = [];
    await runWindowLoop(
      videoId,
      startOffset,
      Infinity,
      startCursor,
      () => true,
      (batch, lastOffset, nextCursor) => {
        for (const c of batch) comments.push(c);
        // Sequential mode preserves the prior contract: emit nextCursor (string|null)
        // so the caller persists it for further resumes.
        if (onBatch) onBatch(batch, nextCursor);
        if (onProgress) onProgress({ count: comments.length, currentSec: lastOffset, totalSec });
      },
    );
    return { ...videoMeta, comments, wasParallel: false as const };
  }

  // Parallel branch: split the VOD into N windows by total length and run their cursor
  // loops concurrently. Windows arrive out-of-order; the caller's onBatch may receive
  // pages from window #5 before window #2's first page lands. Caller must therefore not
  // assume monotonic content_offset_seconds during streaming. After Promise.all, we do
  // ONE k-way-merge + dedup pass and return the sorted/deduped list as `comments` —
  // the caller swaps that in as the source of truth (replacing its streaming
  // accumulator). Cursor argument to onBatch is always null in this branch — cursor
  // resume is single-pointer by design and we don't try to persist N cursors.
  //
  // N tuning: ~20 min/window keeps each worker doing ~20 min of chat (~few hundred pages
  // for chatty streams) while staying inside the browser's HTTP/2 connection multiplex
  // budget for one origin. Cap at 8 — Chrome typically allows ~6 concurrent fetches per
  // origin before queuing, but H2 multiplexes them, and Twitch GQL hasn't shown 429 in
  // practice at this concurrency. Floor 2 ensures every parallel-mode call actually
  // fans out; degenerate-to-1 short-circuits would be silent-undermine-the-feature.
  const N = Math.min(32, Math.max(2, Math.ceil(totalSec / 600)));
  const windowEdges: number[] = [];
  for (let k = 0; k <= N; k++) {
    windowEdges.push(Math.floor((totalSec * k) / N));
  }

  const windowComments: TwitchComment[][] = Array.from({ length: N }, () => []);
  const windowLastOffset: number[] = windowEdges.slice(0, N);
  let totalCount = 0;

  await Promise.all(windowEdges.slice(0, N).map((startSecK, k) => {
    const endSec = k === N - 1 ? Infinity : windowEdges[k + 1];
    return runWindowLoop(
      videoId,
      startSecK,
      endSec,
      null,
      () => true,
      (batch, lastOffset, _nextCursor) => {
        windowComments[k].push(...batch);
        windowLastOffset[k] = lastOffset;
        totalCount += batch.length;
        // Stream the page upward — caller appends to its accumulator without sorting.
        // We pass null cursor: parallel-mode resume falls back to offset-based fallback
        // (single-cursor doesn't make sense across N workers).
        if (onBatch) onBatch(batch, null);
        if (onProgress) {
          // Sum-of-window-coverage gives a monotonically rising currentSec that maxes
          // at totalSec. Each window contributes (lastOffset - startSec) of progress;
          // sum across all windows == effective seconds covered from the timeline.
          let coveredSec = 0;
          for (let i = 0; i < N; i++) {
            coveredSec += Math.max(0, windowLastOffset[i] - windowEdges[i]);
          }
          onProgress({ count: totalCount, currentSec: Math.min(coveredSec, totalSec), totalSec });
        }
      },
    );
  }));

  // One global sort + dedup. Each window array is monotonic internally (Twitch cursor
  // chain), so k-way merge is O(total*N). Dedup tuple-keys absorb boundary doubles
  // (windows K and K+1 both pulling the same offset_int=endSec_K comments).
  const merged = kWayMergeByOffset(windowComments);
  const deduped = dedupCommentsByKey(merged);

  return { ...videoMeta, comments: deduped, wasParallel: true as const };
}

interface ProcessedChatData {
  processedBins: ActivityBin[];
  allComments: TwitchComment[];
  maxActivity: number;
  streamerName: string;
  videoGame: string;
  videoId: string | number | null;
  streamCreatedAt: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class TwitchChatService {
  private readonly state = inject(TwitchChatStateService);
  private readonly geminiService = inject(GeminiService);
  private readonly firebaseService = inject(FirebaseService);
  private readonly libraryService = inject(LibraryService);
  private readonly markersService = inject(MarkersService);
  private readonly syncService = inject(SyncService);
  private readonly twitchApiService = inject(TwitchApiService);
  private readonly injector = inject(Injector);

  private readonly BIN_DURATION_SECONDS = 15;
  readonly MIN_MESSAGES_FOR_PEAK = 5;

  // Approximate bitrate for 1080p60 Source Twitch VODs.
  // Empirical fit ~7.2 Mbps. History: 0.75 (6 Mbps) underestimated, 1.1 (8.8 Mbps)
  // overshot by ~20% (UI 2710 MB vs actual 2200 MB on the same clip). 0.9 lands
  // within ~5% across three streamers measured 2026-05-12. Re-tune if a different
  // resolution/encoder ladder consistently misses.
  private readonly MB_PER_SECOND = 0.9;

  // --- Search constants ---
  private readonly SEARCH_MAX_WINDOW_SECONDS = 60;   // cap search-cluster duration; long flame sessions split into separate moments
  private readonly SEARCH_DUP_TOLERANCE_SECONDS = 30; // skip new search hits that collide with an existing highlight's startTime
  private readonly SEARCH_MAX_SAMPLES = 15;

  // --- Streaming chat download (loadFromUrl) ---
  private readonly STREAM_RECALC_DEBOUNCE_MS = 500;
  private pendingStreamRecalcTimer: ReturnType<typeof setTimeout> | null = null;
  // First recalc on a fresh stream uses autoThreshold=true to seed a sensible default
  // from the partial-data bin distribution; subsequent recalcs preserve user tuning.
  private hasDoneFirstStreamRecalc = false;
  // Monotonic session id incremented on each loadFromUrl. Stream callbacks (onBatch,
  // onMeta, onProgress) capture the session at start and silently drop their work if
  // a new download has begun — without this, picking a new VOD from the library while
  // the previous download is mid-flight let the OLD callbacks mutate state already reset
  // for the NEW VOD (intermingled chatComments, threshold from wrong distribution).
  private currentDownloadSession = 0;

  // --- Live-stream poll loop ---
  // Visible-tab cadence: 90s — far enough below Helix /streams quota concerns
  // (1 call/poll → ~40 calls/hour per user is negligible) and short enough that
  // chat fills in within ~1 bin (15s) of arriving. Hidden-tab cadence stretched
  // to 5 min so backgrounded tabs don't drain mobile battery; a foreground-bring
  // immediately resumes normal cadence on the next tick.
  private readonly LIVE_POLL_INTERVAL_MS = 90 * 1000;
  private readonly LIVE_POLL_INTERVAL_HIDDEN_MS = 5 * 60 * 1000;
  // Safety net against false-positive "live" detection: if 3 ticks in a row produce
  // 0 new chat AND 0 duration growth, exit the loop and finalize. Empirically a
  // truly-live stream advances by ≥15s per ~90s tick (chat at minimum ticks once).
  private readonly LIVE_POLL_MAX_EMPTY_TICKS = 3;
  // Window of recent comments checked for tail-overlap dedup on offset-based resume.
  // Each live tick may include the boundary second's comments again (Twitch GQL Int
  // param truncates fractional seconds). 200 covers ~10s of even xQc-tier traffic.
  private readonly LIVE_DEDUP_WINDOW = 200;

  private potentialClusters = signal<PeakCluster[]>([]);

  potentialPeakBinTimes = computed(() => {
    const times = new Set<number>();
    for (const cluster of this.potentialClusters()) {
      for (let t = cluster.startTime; t <= cluster.endTime; t += this.BIN_DURATION_SECONDS) {
        times.add(t);
      }
    }
    return times;
  });

  potentialHighlightsCount = computed(() => {
    return this.potentialClusters().length;
  });

  // «Найдено: N · в области M» — null when no region is active.
  potentialHighlightsInRegionCount = computed(() => {
    const region = this.state.activeRegion();
    if (!region) return null;
    return this.potentialClusters().filter(c => overlapsRegion(c.startTime, c.endTime, region)).length;
  });

  // Tracks the videoId we last fetched user-clips for. Without this guard the
  // auto-trigger effect would re-fire on every signal touch in its dep set.
  private lastUserClipsFetchedVideoId: string | number | null = null;
  private readonly USER_CLIPS_RETRY_DELAY_MS = 15_000;
  // fetchAndMergeUserClips runs from several triggers (auto-trigger, finalize, live
  // ticks) that can overlap; a result older than the one already applied is dropped.
  private userClipsFetchSeq = 0;
  private userClipsAppliedSeq = 0;

  // Same idea for stream-markers — guards the auto-trigger effect that lands
  // markers on the heatmap as soon as videoId+streamCreatedAt+isProcessed are
  // ready, instead of waiting for the first live-poll tick (~90s on live VODs).
  private lastMarkersFetchedVideoId: string | number | null = null;

  // VOD categories for the heatmap zoom bar: once per opened VOD (+ every live-poll tick).
  private lastChaptersFetchedVideoId: string | number | null = null;
  private chaptersFetchSeq = 0;

  constructor() {
    // Auto-recompute potentialClusters when bins or the threshold change.
    // Existing setter paths (slider, recalculateAnalytics) call
    // updatePotentialClusters() synchronously themselves, but state restoration
    // (loadStateFromLocalStorage, loadStateObject, IDB hydrate) only writes the
    // signals — without this effect, the saved threshold + bins came back but
    // the heatmap stayed empty and "Найдено: 0" until the user touched the
    // slider. The effect is idempotent: running it twice in a row produces the
    // same clusters set, so the redundancy on slider-driven paths is harmless.
    effect(() => {
      // Track both deps via signal reads.
      this.state.activityBins();
      this.state.peakMessageThreshold();
      this.updatePotentialClusters();
    });

    // Auto-fetch user-clips on any path that lands a processed VOD into state:
    // fresh loadFromUrl (already calls fetchAndMergeUserClips inline, this is
    // a defensive no-op via lastUserClipsFetchedVideoId), loadStateObject from
    // library, IDB hydrate on app reload, or any future path. Effect fires once
    // per videoId — switching VOD resets the guard. On the library-open / reload
    // paths this is the ONLY fetch, so a failed attempt gets one delayed retry
    // (before that, КЛИПЫ stayed empty until the VOD was reopened).
    effect(() => {
      const videoId = this.state.videoId();
      const isProcessed = this.state.isProcessed();
      // Wait for Firebase auth-restore: on cold reload IDB-hydrate fires this
      // effect before currentUser populates, and the Helix fallback
      // (/get-vod-clips) needs the Bearer ID token. Re-evaluates when currentUser
      // flips non-null.
      const user = this.firebaseService.currentUser();
      if (!videoId || !isProcessed || !user) return;
      if (this.lastUserClipsFetchedVideoId === videoId) return;
      this.lastUserClipsFetchedVideoId = videoId;
      untracked(async () => {
        for (const delayMs of [0, this.USER_CLIPS_RETRY_DELAY_MS]) {
          if (delayMs) await new Promise(r => setTimeout(r, delayMs));
          if (this.state.videoId() !== videoId) return;
          if (await this.fetchAndMergeUserClips()) return;
        }
      });
    });

    // Categories (game changes) for the heatmap zoom bar — same once-per-VOD guard and one
    // delayed retry as the clips effect. Anonymous GQL, so no wait for auth.
    effect(() => {
      const videoId = this.state.videoId();
      const isProcessed = this.state.isProcessed();
      if (!videoId || !isProcessed) return;
      if (this.lastChaptersFetchedVideoId === videoId) return;
      this.lastChaptersFetchedVideoId = videoId;
      untracked(async () => {
        for (const delayMs of [0, this.USER_CLIPS_RETRY_DELAY_MS]) {
          if (delayMs) await new Promise(r => setTimeout(r, delayMs));
          if (this.state.videoId() !== videoId) return;
          if (await this.fetchVodChapters()) return;
        }
      });
    });

    // Auto-fetch stream-markers — mirrors the user-clips effect but additionally
    // depends on streamCreatedAt (without it fetchAndMergeStreamMarkers falls
    // back to "now - maxOffset", landing markers in the wrong VOD). Closes the
    // 90s gap on live VODs where the only prior trigger was the first
    // runLivePollLoop tick. File-import / loadStateObject / IDB-hydrate paths
    // also benefit — inline calls there become idempotent no-ops via the guard.
    effect(() => {
      const videoId = this.state.videoId();
      const isProcessed = this.state.isProcessed();
      const streamCreatedAt = this.state.streamCreatedAt();
      if (!videoId || !isProcessed || !streamCreatedAt) return;
      if (this.lastMarkersFetchedVideoId === videoId) return;
      this.lastMarkersFetchedVideoId = videoId;
      untracked(() => {
        this.fetchAndMergeStreamMarkers().catch(e => {
          console.warn('[markers] auto-trigger fetch failed:', e);
        });
      });
    });

    // Clear pending seek hint when a load fails — otherwise the next VOD opened
    // via any path would inherit the orphaned offset from the prior failed
    // clip-click. Effect-based instead of touching every error-set call site.
    effect(() => {
      const err = this.state.error();
      if (err && this.state.pendingSeekAfterLoad() !== null) {
        untracked(() => this.state.pendingSeekAfterLoad.set(null));
      }
    });
  }

  async onFileSelected(file: File): Promise<void> {
    this.state.resetState();
    this.state.fileName.set(file.name);

    try {
      const fileContent = await file.text();
      if (file.name.toLowerCase().endsWith('.json')) {
        await this.processFileContent(fileContent, file.name);
      } else if (file.name.toLowerCase().endsWith('.csv')) {
        this.state.pendingCsvContent.set(fileContent);
        this.state.isCsvMode.set(true);
      } else {
        this.state.error.set('Неподдерживаемый формат файла. Пожалуйста, выберите .json или .csv файл.');
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Произошла неизвестная ошибка.';
      this.state.error.set(`Ошибка при чтении файла: ${message}`);
    }
  }

  /**
   * Persist any in-flight partial chat download for the CURRENT VOD to IDB and bump
   * the session id so existing onBatch/onProgress/onMeta callbacks become inert
   * (they no-op via sessionAlive()). Call before any operation that replaces the
   * active project in state — loadStateObject for a different VOD, resetState, or
   * a fresh loadFromUrl. Without this, late-arriving GQL pages from the old VOD
   * mutate state already overwritten with the new VOD's data.
   *
   * Returns the videoId of the just-superseded download (or null if nothing was
   * in flight) so callers can log / debug.
   */
  supersedePartialDownload(): string | null {
    let supersededVideoId: string | null = null;
    if (this.state.isChatDownloading()
        && this.state.videoId()
        && this.state.chatComments().length > 0) {
      supersededVideoId = String(this.state.videoId());
      this.state.chatDownloadComplete.set(false);
      this.state.saveStateForResume();
    }
    this.currentDownloadSession++;
    if (this.pendingStreamRecalcTimer) {
      clearTimeout(this.pendingStreamRecalcTimer);
      this.pendingStreamRecalcTimer = null;
    }
    // Clear in-progress UI flags eagerly — the old session's finally block is gated
    // by sessionAlive() and won't run these resets. Without this, switching to a
    // saved-state VOD (loadStateObject path, no fresh loadFromUrl) leaves a frozen
    // progress bar from the prior VOD with the elapsed-time tick still ringing.
    // For paths that immediately call loadFromUrl after this method, the flags
    // are re-set within the same tick, so the brief flip is invisible.
    this.state.progressInfo.set(null);
    this.state.isChatDownloading.set(false);
    this.state.isLoading.set(false);
    this.state.parallelDownloadActive.set(false);
    return supersededVideoId;
  }

  async loadFromUrl(url: string): Promise<void> {
    const incomingVideoId = url.replace(/\D/g, '') || url;

    // Resume mode: we just loaded a saved project (loadStateObject in app.component)
    // for this VOD whose chatDownloadComplete flag is false. chatComments is already
    // populated; we need to continue the GQL loop from the persisted cursor (or the
    // last comment's offset as a fallback) instead of starting from scratch.
    const isResume = !this.state.chatDownloadComplete()
      && String(this.state.videoId()) === incomingVideoId
      && this.state.chatComments().length > 0;

    // Redownload mode: same VOD, no chat locally (typical after Cloud Sync pull —
    // chatComments is never synced) but analyzed bins/highlights ARE present from
    // the saved state. The «Скачать чат» CTA fires this path. Download from scratch
    // but PRESERVE analyzed state — analysisCache + highlightStateCache survive,
    // and the post-download recalc rebuilds clusters with identical signatures
    // (same chat → same bins → same clusters), so AI descriptions, pinned/deleted
    // flags, custom clip ranges, AI deep-dive / search / merged highlights all
    // re-emerge intact via updateHighlightsFromClusters.
    const isRedownload = !isResume
      && String(this.state.videoId()) === incomingVideoId
      && this.state.chatComments().length === 0
      && this.state.activityBins().length > 0;

    // skipReset == "we are augmenting the current project, not replacing it".
    const skipReset = isResume || isRedownload;

    // If a download for a DIFFERENT VOD is in flight, snapshot its partial state +
    // bump the session id so its callbacks no-op. supersedePartialDownload also
    // clears any pending recalc timer. Skip when augmenting current VOD.
    if (!skipReset
        && this.state.videoId()
        && String(this.state.videoId()) !== incomingVideoId) {
      this.supersedePartialDownload();
    }

    // Bump session and capture the new id. The supersedePartialDownload above already
    // bumped if there was an in-flight; this second bump (when no supersede happened)
    // still gives us a fresh session id for this loadFromUrl invocation.
    const session = ++this.currentDownloadSession;
    const sessionAlive = () => session === this.currentDownloadSession;

    if (!skipReset) {
      this.state.resetState();
    } else {
      // Clear transient banners that may have lingered from a prior interaction
      // (analysis error, info message). Everything else — highlights, bins,
      // caches, video metadata, threshold — stays intact.
      this.state.error.set('');
      this.state.infoMessage.set('');
    }
    this.state.fileName.set(
      isResume ? 'Возобновление загрузки...'
      : isRedownload ? 'Дозагрузка чата...'
      : 'Скачивание с Twitch...');
    this.state.isLoading.set(true);
    this.state.loadingMessage.set(
      isResume ? 'Возобновление загрузки чата...'
      : isRedownload ? 'Загрузка чата для сохранённого анализа...'
      : 'Скачивание чата с Twitch...');
    this.state.isChatDownloading.set(true);
    this.state.chatDownloadComplete.set(false);
    // Parallel-window mode kicks in for fresh downloads + redownloads (full VOD from
    // offset 0). Resume from a prior partial stays sequential — the orchestrator's
    // isSequential branch in downloadChatClientSide reads (startCursor || startOffset>0)
    // and routes there. We mirror that intent here so AI Quick gates correctly.
    this.state.parallelDownloadActive.set(!isResume);
    // Resume / redownload: skip the auto-threshold reseed. For resume we already
    // have a tuned threshold from the prior run (possibly user-adjusted); for
    // redownload we MUST keep the saved threshold, otherwise auto-pick on the
    // partial-data distribution would shift cluster boundaries and orphan
    // analyzed peaks. Fresh: let the first batch's recalc pick one.
    this.hasDoneFirstStreamRecalc = skipReset;

    // Footer progress bar — chat download is the longest visible operation
    // (tens of seconds for big VODs). GQL doesn't tell us the total comment
    // count, but each comment carries contentOffsetSeconds and the VOD has a
    // known lengthSeconds, so we derive percent from offset / length (the
    // same trick TwitchDownloaderWPF uses). Until the first batch lands the
    // bar runs indeterminate.
    this.state.progressInfo.set({
      label: isRedownload ? 'Дозагрузка чата' : 'Скачивание чата',
      detail: '0 сообщений',
      startedAt: performance.now(),
    });

    try {
      // Resume snapshot — captured before potential resetState. We need both for the
      // GQL call: cursor (preferred, opaque pagination token) and lastOffset (fallback
      // when cursor never landed before the prior abort).
      const startCursor = isResume ? this.state.chatDownloadCursor() : null;
      const startOffset = isResume
        ? Math.max(0, ...this.state.chatComments().map(c => c.content_offset_seconds || 0))
        : 0;

      const data = await downloadChatClientSide(
        incomingVideoId,
        ({ count, currentSec, totalSec }) => {
          if (!sessionAlive()) return;
          const percent = totalSec > 0
            ? Math.min(100, Math.max(0, (currentSec / totalSec) * 100))
            : undefined;
          this.state.progressInfo.update(p => p ? {
            ...p,
            percent,
            detail: `${count.toLocaleString('ru-RU')} сообщений`,
          } : p);
        },
        skipReset ? undefined : (videoInfo) => {
          if (!sessionAlive()) return;
          // Early UI flip — show player + sidebar shell before chat download completes.
          // The player iframe init is gated on `videoId() && isProcessed()`, and the right
          // sidebar gate is `isProcessed()` — both flip on now so the user can watch the
          // VOD while chat downloads in background. Bins/highlights still empty until the
          // first batch lands; loadingMessage stays so user sees what's happening.
          // In resume / redownload mode this callback is omitted — videoId/title/etc are
          // already set from loadStateObject and re-flipping them would clobber any user
          // edits made in the library.
          this.state.videoId.set(String(videoInfo.id));
          this.state.streamerName.set(videoInfo.owner?.displayName || null);
          this.state.streamerLogin.set(videoInfo.owner?.login?.toLowerCase() || null);
          this.state.videoTitle.set(videoInfo.title || null);
          this.state.videoGame.set(videoInfo.game?.name || null);
          this.state.streamCreatedAt.set(videoInfo.createdAt || null);
          this.state.isProcessed.set(true);
        },
        (batch, cursor) => {
          if (!sessionAlive()) return;
          // Stream-append each GQL page so heatmap/clusters fill in progressively. Each
          // batch is 30-100 messages; running the full filter+bin pass on every page would
          // pin the main thread on big chats — debounced via scheduleStreamRecalc.
          this.state.chatComments.update(prev => prev.concat(batch));
          // Persist cursor in state so a supersede/abort/tab-close can pick it up via
          // saveStateForResume on the next loadFromUrl. cursor=null means we just hit
          // the natural end of the chat (`hasNextPage=false` page).
          this.state.chatDownloadCursor.set(cursor);
          this.scheduleStreamRecalc();
        },
        startCursor,
        startOffset,
      );

      // In resume / redownload mode, ensure isProcessed flips on (loadStateObject
      // already set it, but defensive set guards against any path that left it false).
      if (skipReset && sessionAlive()) {
        this.state.isProcessed.set(true);
      }

      // If a newer loadFromUrl took over while we were awaiting, bail out before the
      // post-download work clobbers the new session's state.
      if (!sessionAlive()) return;

      // Parallel orchestrator returns its own sorted+deduped chatComments — replace the
      // streaming accumulator (which received batches out-of-order across windows) so
      // recalc + AI + sync see a properly ordered list. Sequential branch streams in
      // order via onBatch and `data.wasParallel` is false, so this is a no-op there.
      if (data.wasParallel) {
        this.state.chatComments.set(data.comments);
        // Recalc with the sorted set — AI Quick is about to be re-enabled and we want
        // it running on bins built from the canonical comment order.
        this.scheduleStreamRecalc();
      }
      // Drop the parallel-phase gate. If runLivePollLoop runs next, it operates
      // sequentially and the existing single-boundary Quick semantics apply.
      this.state.parallelDownloadActive.set(false);

      // Live-stream check. The GQL loop just exited because Twitch returned
      // hasNextPage=false — but for a CURRENTLY-LIVE VOD that just means we caught
      // up to the present, not that recording is done. Hit /get-vod-status: if the
      // VOD's stream_id matches the broadcaster's active live stream, enter the
      // poll loop and keep chatDownloadComplete=false until the stream actually
      // ends (or the safeguard trips). Per-VOD live check ensures opening an OLD
      // VOD while the streamer is currently broadcasting a NEW stream returns
      // isLive=false (different stream_id) → no poll loop, classic finalize.
      const initialStatus = await this.fetchVodLiveStatus(incomingVideoId);
      if (!sessionAlive()) return;

      if (initialStatus?.userId) {
        this.state.broadcasterId.set(initialStatus.userId);
      }

      if (initialStatus?.isLive) {
        this.state.isLive.set(true);
        this.state.liveStreamId.set(initialStatus.currentLiveStreamId || initialStatus.streamId || null);
        if (initialStatus.lengthSeconds > 0) {
          this.state.liveDurationSec.set(initialStatus.lengthSeconds);
        }
        // Full-window marker fetch now that catch-up is complete. The constructor's
        // auto-trigger effect already fired once (right after onMeta, chatComments
        // empty → 1h window), and live VODs skip finalizeStreamedDownload (the path
        // that re-fetches with full data for finalized VODs) because they enter the
        // poll loop instead. Without this call, markers past the first hour wouldn't
        // surface until the first live-poll tick (up to 90s, or 5min when the tab is
        // hidden) — the «open live stream, markers don't appear until I switch VODs
        // and come back» bug. chatComments now reflects the live present and
        // liveDurationSec is set, so the widened window in fetchAndMergeStreamMarkers
        // covers the whole stream. Runs before saveStateForResume so a tab-close
        // before the first tick still persists the merged markers.
        try {
          await this.fetchAndMergeStreamMarkers();
        } catch (e) {
          console.warn('[live] initial full-window marker fetch failed:', e);
        }
        if (!sessionAlive()) return;
        // Snapshot the current resume state once before the loop in case a tab close
        // happens before the first tick (tail-of-stream scenario). saveStateForResume
        // bypasses the isChatDownloading autosave gate.
        this.state.saveStateForResume();
        await this.runLivePollLoop(incomingVideoId, sessionAlive);
        if (!sessionAlive()) return;
      }
      this.state.isLive.set(false);
      this.state.liveDurationSec.set(0);

      // Reaching this line means: (a) live-poll exited because stream ended /
      // safeguard tripped, or (b) the VOD was never live to begin with. Either
      // way the chat is in its final state — mark the download complete and clear
      // the resume cursor.
      this.state.chatDownloadComplete.set(true);
      this.state.chatDownloadCursor.set(null);

      // Cancel any pending debounced recalc — finalize() will do the conclusive pass.
      if (this.pendingStreamRecalcTimer) {
        clearTimeout(this.pendingStreamRecalcTimer);
        this.pendingStreamRecalcTimer = null;
      }

      // chatComments holds the FULL set of comments (prior partial in resume mode +
      // streamed batches + any live-poll appends). Empty here means: not a live VOD
      // (live-poll didn't run) AND the GQL loop returned nothing — genuinely empty.
      if (this.state.chatComments().length === 0) {
        this.state.error.set('Чат не найден или пуст.');
        return;
      }

      const fileName = `twitch_chat_${data.video?.id || 'unknown'}.json`;

      // Mark chat as downloaded + create/update the project in library. Chat is kept in
      // IDB (full state) and pushed to Cloud Sync (without raw comments) — there's no
      // separate on-disk copy anymore, so chatFilePath is just the synthetic fileName.
      if (data.streamer?.login || data.video?.user_login) {
        const streamerLogin = (data.streamer?.login || data.video?.user_login || '').toLowerCase();
        const channel = this.libraryService.savedChannels().find(c => c.login === streamerLogin);

        if (channel && data.video?.id) {
          const vid = String(data.video.id);
          this.libraryService.markChatAsDownloaded(vid, fileName);

          if (!this.libraryService.getProject(vid)) {
            this.libraryService.saveProject({
              videoId: vid,
              channelId: channel.id,
              title: data.video?.title || '',
              createdAt: data.video?.created_at || new Date().toISOString(),
              duration: data.video?.duration || '0h0m0s',
              thumbnailUrl: data.video?.thumbnail_url || '',
              isChatDownloaded: true,
              chatFilePath: fileName,
              lastModifiedAt: Date.now()
            });
          }
        }
      }

      // Finalize without re-parsing — chatComments already populated via onBatch.
      await this.finalizeStreamedDownload(data, fileName);

    } catch (error: any) {
      console.error('Ошибка при скачивании чата:', error);
      if (sessionAlive()) {
        this.state.error.set(`Не удалось скачать чат: ${error.message}`);
      }
    } finally {
      // Only the active session may flip the global flags off — otherwise an old
      // download finishing AFTER a new one started would prematurely declare loading
      // done, hiding the new session's progress bar.
      if (sessionAlive()) {
        this.state.progressInfo.set(null);
        this.state.isChatDownloading.set(false);
        this.state.isLoading.set(false);
        this.state.parallelDownloadActive.set(false);
        if (this.pendingStreamRecalcTimer) {
          clearTimeout(this.pendingStreamRecalcTimer);
          this.pendingStreamRecalcTimer = null;
        }
      }
    }
  }

  private scheduleStreamRecalc(): void {
    if (this.pendingStreamRecalcTimer) return; // already scheduled within debounce window
    this.pendingStreamRecalcTimer = setTimeout(() => {
      this.pendingStreamRecalcTimer = null;
      // First recalc seeds an autoThreshold from the partial-data distribution. After
      // that, autoThreshold=false so user-tuned slider values aren't clobbered on each
      // new batch (and the final post-download recalc also stays at false).
      this.recalculateAnalytics(!this.hasDoneFirstStreamRecalc);
      this.hasDoneFirstStreamRecalc = true;
      // recalculateAnalytics overwrites loadingMessage — restore the user-facing
      // download status so the sidebar footer stays coherent during streaming.
      if (this.state.isChatDownloading()) {
        this.state.loadingMessage.set(
          this.state.isLive() ? 'В прямом эфире — пополняется чат...' : 'Скачивание чата с Twitch...'
        );
      }
    }, this.STREAM_RECALC_DEBOUNCE_MS);
  }

  /**
   * Calls /get-vod-status to determine whether the VOD is currently being recorded
   * live (stream_id of the VOD === currently-live stream id for the same broadcaster).
   * Returns null on any failure — caller treats that as "treat as offline" so a
   * backend hiccup never traps the UI in live-poll mode.
   */
  private async fetchVodLiveStatus(videoId: string): Promise<{
    isLive: boolean;
    lengthSeconds: number;
    streamId: string | null;
    currentLiveStreamId: string | null;
    userId: string | null;
  } | null> {
    const cloudRunUrl = this.firebaseService.cloudRunApiUrl;
    if (!cloudRunUrl) return null;
    const headers = await this.firebaseService.buildCloudRunHeaders();
    if (!headers) return null;
    try {
      const resp = await fetch(`${cloudRunUrl}/get-vod-status`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoId }),
      });
      if (!resp.ok) {
        console.warn('[live] /get-vod-status returned', resp.status);
        return null;
      }
      const json = await resp.json();
      return {
        isLive: !!json.isLive,
        lengthSeconds: typeof json.lengthSeconds === 'number' ? json.lengthSeconds : 0,
        streamId: json.streamId ?? null,
        currentLiveStreamId: json.currentLiveStreamId ?? null,
        userId: json.userId ?? null,
      };
    } catch (e) {
      console.warn('[live] failed to fetch VOD status:', e);
      return null;
    }
  }

  /**
   * Strip incoming comments that match (offset, body, commenter) of any in the last
   * LIVE_DEDUP_WINDOW already-stored comments. Used after offset-based resume in the
   * live-poll loop, where Twitch's `contentOffsetSeconds` Int param can re-include
   * the boundary second's messages on the next tick. Without this, every live tick
   * would double-count messages at the resume boundary, inflating heatmap bins.
   */
  private dedupAgainstTail(batch: TwitchComment[]): TwitchComment[] {
    if (batch.length === 0) return batch;
    const tail = this.state.chatComments().slice(-this.LIVE_DEDUP_WINDOW);
    if (tail.length === 0) return batch;
    const seen = new Set(tail.map(commentDedupKey));
    return batch.filter(c => !seen.has(commentDedupKey(c)));
  }

  /**
   * Live-stream chat polling loop. Entered after the first downloadChatClientSide
   * pass when /get-vod-status confirms the VOD is currently live (its stream_id
   * matches the broadcaster's active stream id). Each tick:
   *   1. Sleep LIVE_POLL_INTERVAL_MS (or HIDDEN_MS if tab is in background).
   *   2. Re-check live status via /get-vod-status; bail + finalize if offline.
   *   3. Resume chat download from last cursor (or `floor(maxOffset)` fallback).
   *   4. Dedup tail overlap, append new comments, debounced recalc.
   *   5. Re-fetch markers under the now-extended VOD window, snapshot resume state.
   *   6. If LIVE_POLL_MAX_EMPTY_TICKS in a row produced no new chat AND no
   *      duration growth, treat as false-positive and force-exit (safety net).
   *
   * Returns when: live=false, sessionAlive=false, safeguard tripped, or repeated
   * unrecoverable error. Caller's finalize block runs after return.
   */
  private async runLivePollLoop(
    videoId: string,
    sessionAlive: () => boolean,
  ): Promise<void> {
    let emptyTicks = 0;
    let lastDuration = 0;
    let lastBoundaryOffset = this.state.chatComments().length > 0
      ? this.state.chatComments()[this.state.chatComments().length - 1].content_offset_seconds
      : 0;

    // Initial footer flip — shows "В прямом эфире" instead of percent immediately,
    // before the first tick even fires (so user understands why the bar dropped %).
    this.state.progressInfo.update(p => p ? {
      ...p,
      label: 'В прямом эфире',
      percent: undefined,
      detail: `${this.state.chatComments().length.toLocaleString('ru-RU')} сообщений`,
    } : p);
    this.state.loadingMessage.set('В прямом эфире — пополняется чат...');

    while (sessionAlive()) {
      const interval = (typeof document !== 'undefined' && document.visibilityState === 'hidden')
        ? this.LIVE_POLL_INTERVAL_HIDDEN_MS
        : this.LIVE_POLL_INTERVAL_MS;

      await new Promise(r => setTimeout(r, interval));
      if (!sessionAlive()) return;

      const status = await this.fetchVodLiveStatus(videoId);
      if (!sessionAlive()) return;

      if (!status) {
        emptyTicks++;
        if (emptyTicks >= this.LIVE_POLL_MAX_EMPTY_TICKS) {
          console.warn('[live] safeguard: /get-vod-status unreachable for too long, exiting');
          return;
        }
        continue;
      }

      this.state.isLive.set(status.isLive);
      this.state.liveStreamId.set(status.currentLiveStreamId || status.streamId || null);
      if (typeof status.lengthSeconds === 'number' && status.lengthSeconds > 0) {
        this.state.liveDurationSec.set(status.lengthSeconds);
      }

      if (!status.isLive) {
        console.log('[live] stream ended → exiting poll loop, will finalize');
        return;
      }

      const cursor = this.state.chatDownloadCursor();
      const beforeCount = this.state.chatComments().length;
      const startOffset = cursor ? 0 : Math.max(0, Math.floor(lastBoundaryOffset));

      try {
        const session = this.currentDownloadSession;
        await downloadChatClientSide(
          videoId,
          () => {
            if (session !== this.currentDownloadSession) return;
            this.state.progressInfo.update(p => p ? {
              ...p,
              label: 'В прямом эфире',
              percent: undefined,
              detail: `${this.state.chatComments().length.toLocaleString('ru-RU')} сообщений`,
            } : p);
          },
          undefined, // onMeta — already populated; don't clobber user edits to title/streamer
          (batch, nextCursor) => {
            if (session !== this.currentDownloadSession) return;
            const fresh = this.dedupAgainstTail(batch);
            if (fresh.length > 0) {
              this.state.chatComments.update(prev => prev.concat(fresh));
              const last = fresh[fresh.length - 1];
              if (typeof last.content_offset_seconds === 'number') {
                lastBoundaryOffset = last.content_offset_seconds;
              }
            }
            this.state.chatDownloadCursor.set(nextCursor);
            this.scheduleStreamRecalc();
          },
          cursor,
          startOffset,
        );
      } catch (e) {
        console.warn('[live] download tick failed, will retry next interval:', e);
        emptyTicks++;
        if (emptyTicks >= this.LIVE_POLL_MAX_EMPTY_TICKS) {
          console.warn('[live] safeguard: too many failed ticks, exiting');
          return;
        }
        continue;
      }
      if (!sessionAlive()) return;

      const afterCount = this.state.chatComments().length;
      const grew = afterCount > beforeCount || status.lengthSeconds > lastDuration;
      if (grew) {
        emptyTicks = 0;
        lastDuration = Math.max(lastDuration, status.lengthSeconds);
        this.state.lastLivePollAt.set(performance.now());
        // Re-match markers under the now-extended VOD window. If a marker landed
        // in the new tail (after the previous fetch's `endTime`), this surfaces it.
        try {
          await this.fetchAndMergeStreamMarkers();
        } catch (e) {
          console.warn('[live] marker re-fetch failed:', e);
        }
        // Re-fetch user clips: community clips made during the new tail surface here.
        try {
          await this.fetchAndMergeUserClips();
        } catch (e) {
          console.warn('[live] user-clips re-fetch failed:', e);
        }
        // A category change during the stream adds a chapter.
        await this.fetchVodChapters();
        // Snapshot resume state so a tab-close mid-loop persists the new tail to IDB.
        // Bypasses the isChatDownloading autosave gate via saveStateForResume.
        this.state.saveStateForResume();
      } else {
        emptyTicks++;
        this.state.lastLivePollAt.set(performance.now());
        if (emptyTicks >= this.LIVE_POLL_MAX_EMPTY_TICKS) {
          console.warn('[live] safeguard: 3 empty ticks (no new chat, no duration growth), exiting');
          return;
        }
      }
    }
  }

  // Conclusive pass after the streaming download completes. chatComments is already
  // fully populated (onBatch appended each page), so we skip processChatLogWithWorker's
  // JSON.parse + sort round-trip — that re-parsing on a 10MB xQc-tier chat string was
  // a 1-2s thread freeze. fetchAndMergeStreamMarkers needs streamCreatedAt + duration,
  // both of which were set in onMeta and through the player READY handler.
  private async finalizeStreamedDownload(data: any, fileName: string): Promise<void> {
    this.state.fileName.set(fileName);

    // Final recalc with full data. autoThreshold=false to preserve any user-tuned
    // threshold from mid-download slider adjustments; if user didn't touch it, the
    // first-batch auto-threshold from scheduleStreamRecalc remains in effect.
    this.recalculateAnalytics(false);

    await this.fetchAndMergeStreamMarkers();
    await this.fetchAndMergeUserClips();

    // Mark in library again (idempotent) — covers the case where the channel-folder
    // path above didn't fire (no matching channel) but we still want library tracking.
    if (data.video?.id) {
      this.libraryService.markChatAsDownloaded(String(data.video.id), fileName);
    }

    // isProcessed already true from onMeta; defensive set so the file-import path
    // (if it ever calls finalizeStreamedDownload) isn't broken either.
    this.state.isProcessed.set(true);
  }

  async fetchAndSaveChatBackground(videoUrl: string, videoId: string, channelLogin: string, videoDataFallback: any): Promise<void> {
    // Download chat directly from browser via Twitch GQL API
    const extractedId = videoUrl.replace(/\D/g, '') || videoId;
    const data = await downloadChatClientSide(extractedId);

    if (!data.comments || data.comments.length === 0) {
      throw new Error('Чат не найден или пуст.');
    }

    const fileName = `twitch_chat_${data.video?.id || videoId}.json`;
    const channel = this.libraryService.savedChannels().find(c => c.login === channelLogin.toLowerCase());

    this.libraryService.markChatAsDownloaded(videoId, fileName);

    if (channel) {
      const project = this.libraryService.getProject(videoId);
      if (!project) {
        this.libraryService.saveProject({
          videoId: String(data.video?.id || videoId),
          channelId: channel.id,
          title: data.video?.title || videoDataFallback?.title || 'Unknown',
          createdAt: data.video?.created_at || videoDataFallback?.created_at || new Date().toISOString(),
          duration: data.video?.duration || videoDataFallback?.duration || '0h0m0s',
          thumbnailUrl: data.video?.thumbnail_url || videoDataFallback?.thumbnail_url || '',
          isChatDownloaded: true,
          chatFilePath: fileName,
          lastModifiedAt: Date.now()
        });
      }
    }
  }

  async handleCsvSubmit(): Promise<void> {
    const csvVodId = this.state.csvVodId();
    const pendingCsvContent = this.state.pendingCsvContent();

    if (!csvVodId || !pendingCsvContent) {
      this.state.error.set('Необходимо указать ID видео или ссылку на него.');
      return;
    }

    const match = csvVodId.match(/(?:videos\/)?(\d+)/);
    const extractedVodId = match ? match[1] : csvVodId;

    if (!/^\d+$/.test(extractedVodId)) {
      this.state.error.set('Неверный формат ID видео. Укажите числовой ID или полную ссылку на VOD.');
      return;
    }

    try {
      const comments = this.parseCsvToComments(pendingCsvContent);
      const chatLog: TwitchChatLog = {
        comments,
        streamer: { name: this.state.csvStreamerName() || 'Неизвестно' },
        video: {
          id: extractedVodId,
          title: this.state.csvVideoTitle() || 'Без названия',
          game: this.state.csvVideoGame() || 'Неизвестно'
        }
      };
      await this.processFileContent(JSON.stringify(chatLog), this.state.fileName());

    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Произошла неизвестная ошибка.';
      this.state.error.set(`Ошибка при разборе CSV файла: ${message}`);
    }
  }

  private parseCsvToComments(csvContent: string): TwitchComment[] {
    const lines = csvContent.trim().split(/\r?\n/);
    if (lines.length < 2) return [];

    const dataLines = lines.slice(1);

    return dataLines.map((line, index) => {
      try {
        const parts = [];
        let currentPart = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            parts.push(currentPart);
            currentPart = '';
          } else {
            currentPart += char;
          }
        }
        parts.push(currentPart);

        if (parts.length < 4) {
          throw new Error('Неверное количество колонок.');
        }

        const time = parseInt(parts[0], 10);
        if (isNaN(time)) {
          throw new Error(`Неверный формат времени '${parts[0]}'.`);
        }

        return {
          content_offset_seconds: time,
          commenter: { display_name: parts[1] || 'unknown' },
          message: { body: parts[3]?.replace(/^"|"$/g, '') || '' },
        };
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Неизвестная ошибка.';
        console.error(`Ошибка разбора строки CSV #${index + 2}: ${line}\n${message}`);
        return null;
      }
    }).filter((comment): comment is TwitchComment => comment !== null);
  }

  private async processFileContent(content: string, fileName: string): Promise<void> {
    this.state.fileName.set(fileName);
    this.state.isLoading.set(true);
    this.state.isCsvMode.set(false);
    this.state.loadingMessage.set('Чтение и обработка файла...');

    try {
      await new Promise(res => setTimeout(res, 10));
      // First pass: just read the JSON/CSV to get raw comments
      const processedData: ProcessedChatData = await this.processChatLogWithWorker(content);

      this.state.streamerName.set(processedData.streamerName);
      this.state.videoGame.set(processedData.videoGame);
      this.state.videoId.set(processedData.videoId);
      this.state.streamCreatedAt.set(processedData.streamCreatedAt);

      this.state.chatComments.set(processedData.allComments);

      // Fresh chat load — let recalculateAnalytics auto-pick a sensible
      // peakMessageThreshold based on the bin-count distribution. Without
      // this, big chats (xQc-tier ~100 msgs/bin median) treat every bin as
      // a peak and the heatmap looks uniformly hot.
      this.recalculateAnalytics(true);

      this.state.isProcessed.set(true);

      // After chat is processed and we know the time bounds, fetch markers
      await this.fetchAndMergeStreamMarkers();
      await this.fetchAndMergeUserClips();

      // Mark chat as downloaded in the library
      if (processedData.videoId) {
        this.libraryService.markChatAsDownloaded(String(processedData.videoId), fileName);
      }

    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Произошла неизвестная ошибка.';
      this.state.error.set(`Ошибка при обработке файла: ${message}`);
      this.state.isProcessed.set(false);
    } finally {
      this.state.isLoading.set(false);
    }
  }

  // --- Filtering Logic ---

  updateFilters(newFilters: ChatFilters) {
    this.state.filterSettings.set(newFilters);
    this.recalculateAnalytics();
  }

  addFilterKeyword(keyword: string) {
    if (!keyword) return;
    const current = this.state.filterSettings();
    // Matching is case-insensitive — a second «KEK» filters nothing new, only adds a chip.
    if (current.keywords.some(k => k.toLowerCase() === keyword.toLowerCase())) return;
    const updated = { ...current, keywords: [...current.keywords, keyword] };
    this.updateFilters(updated);
  }

  removeFilterKeyword(keyword: string) {
    const current = this.state.filterSettings();
    const updated = { ...current, keywords: current.keywords.filter(k => k !== keyword) };
    this.updateFilters(updated);
  }

  addIgnoredUser(user: string) {
    if (!user) return;
    const current = this.state.filterSettings();
    if (!current.ignoredUsers.includes(user)) {
      const updated = { ...current, ignoredUsers: [...current.ignoredUsers, user] };
      this.updateFilters(updated);
    }
  }

  removeIgnoredUser(user: string) {
    const current = this.state.filterSettings();
    const updated = { ...current, ignoredUsers: current.ignoredUsers.filter(u => u !== user) };
    this.updateFilters(updated);
  }

  toggleHideSubs(val: boolean) {
    const current = this.state.filterSettings();
    const updated = { ...current, hideSubs: val };
    this.updateFilters(updated);
  }

  /**
   * Applies filters to raw chatComments, rebuilds bins, and updates peaks.
   * Runs synchronously as it's typically fast enough for filtered subsets.
   *
   * `autoThreshold=true` (passed only from a fresh chat load — first download
   * or file import) rescales `peakMessageThreshold` to the 90th percentile of
   * non-zero bin counts so big chats don't paint the entire heatmap yellow on
   * the default threshold of 15. Filter changes pass false so the user's
   * manually-chosen threshold isn't clobbered.
   */
  recalculateAnalytics(autoThreshold: boolean = false) {
    this.state.loadingMessage.set('Применение фильтров и пересчет...');

    const allComments = this.state.chatComments();
    const filters = this.state.filterSettings();

    // 1. Filter
    const subPatterns = [/gifted a Tier/, /subscribed with Prime/, /subscribed at Tier/, /Mystery Gift/];

    const filtered = allComments.filter(c => {
      const body = c.message.body;
      const author = c.commenter.display_name;

      // User block
      if (filters.ignoredUsers.includes(author)) return false;

      // Keywords block
      if (filters.keywords.some(k => body.toLowerCase().includes(k.toLowerCase()))) return false;

      // Subs block
      if (filters.hideSubs) {
        if (subPatterns.some(p => p.test(body))) return false;
      }

      return true;
    });

    this.state.filteredComments.set(filtered);

    // 2. Re-bin
    const bins = new Map<number, ActivityBin>();
    let maxTime = 0;

    for (const comment of filtered) {
      const binIndex = Math.floor(comment.content_offset_seconds / this.BIN_DURATION_SECONDS);
      if (!bins.has(binIndex)) {
        bins.set(binIndex, { startTime: binIndex * this.BIN_DURATION_SECONDS, messageCount: 0, messages: [] });
      }
      const bin = bins.get(binIndex)!;
      bin.messageCount++;
      bin.messages.push(`${comment.commenter.display_name}: ${comment.message.body}`);

      if (comment.content_offset_seconds > maxTime) maxTime = comment.content_offset_seconds;
    }

    const totalBins = Math.floor(maxTime / this.BIN_DURATION_SECONDS);
    const processedBins: ActivityBin[] = [];
    let maxActivity = 0;

    for (let i = 0; i <= totalBins; i++) {
      const bin = bins.get(i) || { startTime: i * this.BIN_DURATION_SECONDS, messageCount: 0, messages: [] };
      processedBins.push(bin);
      if (bin.messageCount > maxActivity) maxActivity = bin.messageCount;
    }

    // 3. Update State
    this.state.activityBins.set(processedBins);
    this.state.maxActivity.set(maxActivity > 0 ? maxActivity : 100);

    if (autoThreshold) {
      // Fresh chat — pick threshold from the bin-count distribution.
      // 90th percentile of non-zero bins gives a sensible "above the noise"
      // value for both small streams (median ~3 → threshold ~10) and huge
      // ones (xQc median ~100 → threshold ~400). Falls back to MIN_MESSAGES
      // when there's not enough data.
      const counts = processedBins
        .map(b => b.messageCount)
        .filter(c => c > 0)
        .sort((a, b) => a - b);
      let auto = this.MIN_MESSAGES_FOR_PEAK;
      if (counts.length > 0) {
        const idx = Math.min(counts.length - 1, Math.floor(counts.length * 0.9));
        auto = Math.max(this.MIN_MESSAGES_FOR_PEAK, counts[idx]);
      }
      this.state.peakMessageThreshold.set(auto);
    } else {
      // Filter change — keep user's chosen threshold, only enforce floor.
      const currentThreshold = this.state.peakMessageThreshold();
      this.state.peakMessageThreshold.set(Math.max(this.MIN_MESSAGES_FOR_PEAK, currentThreshold));
    }

    this.updatePotentialClusters();
    this.updateHighlightsFromClusters();
  }

  /**
   * Helper to find top chatters for "Ignore List" suggestion
   */
  getTopChatters(): { name: string, count: number }[] {
    const counts = new Map<string, number>();
    const comments = this.state.chatComments(); // Check against ALL comments to find spammers

    for (const c of comments) {
      const name = c.commenter.display_name;
      counts.set(name, (counts.get(name) || 0) + 1);
    }

    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));
  }

  // --- Worker ---

  private processChatLogWithWorker(fileContent: string): Promise<ProcessedChatData> {
    return new Promise((resolve, reject) => {
      const workerCode = `
        self.onmessage = function(e) {
          try {
            const { fileContent, binDuration } = e.data;
            
            if (fileContent === undefined || fileContent === null) {
                throw new Error('Содержимое файла не определено');
            }
            
            const trimmedContent = typeof fileContent === 'string' ? fileContent.trim() : '';

            if (!trimmedContent || trimmedContent === "undefined" || trimmedContent === "null") {
                throw new Error('Содержимое файла повреждено (пусто или "undefined")');
            }

            const chatLog = JSON.parse(trimmedContent);

            if (!chatLog.comments || chatLog.comments.length === 0) {
              throw new Error('Файл не содержит комментариев или имеет неверный формат.');
            }

            // Clean and sort all comments for playback
            const allComments = chatLog.comments.map(c => ({
                commenter: { display_name: c.commenter?.display_name || 'unknown' },
                message: { body: c.message?.body || '' },
                content_offset_seconds: c.content_offset_seconds
            })).sort((a, b) => a.content_offset_seconds - b.content_offset_seconds);
            
            // Initial binning (not strictly needed since we re-bin in service, but good for initial stats)
            // returning everything needed
            
            const result = {
              processedBins: [], // Will be calculated by service filter logic
              allComments,
              maxActivity: 0,
              streamerName: chatLog.streamer?.name ?? 'Неизвестно',
              videoTitle: chatLog.video?.title ?? 'Без названия',
              videoGame: chatLog.video?.game ?? 'Неизвестно',
              videoId: chatLog.video?.id ?? null,
              streamCreatedAt: chatLog.video?.created_at ?? null,
            };

            self.postMessage({ data: result });
          } catch (error) {
            self.postMessage({ error: error.message });
          }
        };
      `;

      const blob = new Blob([workerCode], { type: 'application/javascript' });
      const workerUrl = URL.createObjectURL(blob);
      const worker = new Worker(workerUrl);

      worker.onmessage = ({ data }) => {
        if (data.error) {
          reject(new Error(data.error));
        } else {
          resolve(data.data);
        }
        worker.terminate();
        URL.revokeObjectURL(workerUrl);
      };

      worker.onerror = (error) => {
        reject(new Error(`Worker error: ${error.message}`));
        worker.terminate();
        URL.revokeObjectURL(workerUrl);
      };

      worker.postMessage({ fileContent, binDuration: this.BIN_DURATION_SECONDS });
    });
  }

  updatePeakMessageThreshold(threshold: number): void {
    this.state.peakMessageThreshold.set(threshold);
    this.updatePotentialClusters();
    this.updateHighlightsFromClusters();
  }

  updateRewindOffset(offset: number): void {
    this.state.rewindOffsetSeconds.set(offset);
    // Peaks are rebuilt from highlightStateCache (threshold / filter change, stream recalc,
    // live-poll tick, undo replays) — remap it too, or every rebuild reverts Pre-Roll on peaks.
    const cache = this.state.highlightStateCache;
    for (const [t, entry] of cache) {
      if (entry.isStartTimeManuallySet) continue;
      const newClipStart = Math.max(0, t - offset);
      cache.set(t, { ...entry, clipStartTime: newClipStart, clipEndTime: Math.max(entry.clipEndTime, newClipStart + 0.1) });
    }
    this.state.highlights.update(currentHighlights => {
      return currentHighlights.map(highlight => {
        if (highlight.isStartTimeManuallySet) return highlight;
        // Speech segments start on exact line bounds — Pre-Roll is chat-lag compensation, n/a.
        if (isTranscriptSource(highlight.source)) return highlight;
        const newClipStart = Math.max(0, highlight.startTime - offset);
        return {
          ...highlight,
          clipStartTime: newClipStart,
          clipEndTime: Math.max(highlight.clipEndTime, newClipStart + 0.1),
        };
      });
    });
  }

  private updatePotentialClusters(): void {
    const clusters = this._calculatePotentialClusters(this.state.peakMessageThreshold());
    this.potentialClusters.set(clusters);
  }

  private _calculatePotentialClusters(messageThreshold: number): PeakCluster[] {
    const processedBins = this.state.activityBins();
    if (processedBins.length === 0) return [];

    const peakThreshold = Math.max(this.MIN_MESSAGES_FOR_PEAK, messageThreshold);
    const peakBins = processedBins
      .filter(bin => bin.messageCount >= peakThreshold)
      .sort((a, b) => a.startTime - b.startTime);

    if (peakBins.length === 0) return [];

    // Build a Set of peak bin times for fast lookup
    const peakTimes = new Set(peakBins.map(b => b.startTime));
    const binMap = new Map(processedBins.map(b => [b.startTime, b]));

    const clusters: PeakCluster[] = [];
    let currentCluster: PeakCluster | null = null;

    for (const bin of peakBins) {
      if (currentCluster) {
        const gap = bin.startTime - currentCluster.endTime;
        const isAdjacent = gap === this.BIN_DURATION_SECONDS;
        // Allow merging through 1 sub-threshold bin (gap of 2 bins = 30s)
        const isOneGap = gap === this.BIN_DURATION_SECONDS * 2
          && !peakTimes.has(currentCluster.endTime + this.BIN_DURATION_SECONDS);

        if (isAdjacent || isOneGap) {
          // If bridging a gap, include the in-between bin's messages too
          if (isOneGap) {
            const gapBin = binMap.get(currentCluster.endTime + this.BIN_DURATION_SECONDS);
            if (gapBin) {
              currentCluster.messageCount += gapBin.messageCount;
              currentCluster.messages.push(...gapBin.messages);
            }
          }
          currentCluster.endTime = bin.startTime;
          currentCluster.messageCount += bin.messageCount;
          currentCluster.messages.push(...bin.messages);
          continue;
        }
      }

      if (currentCluster) clusters.push(currentCluster);
      currentCluster = {
        startTime: bin.startTime,
        endTime: bin.startTime,
        messageCount: bin.messageCount,
        messages: [...bin.messages],
      };
    }
    if (currentCluster) clusters.push(currentCluster);
    return clusters;
  }

  calculateEstimatedSize(durationSeconds: number): number {
    // 1.1 MB/s is approx 9000kbps (video+audio) for 1080p60 Source
    return parseFloat((durationSeconds * this.MB_PER_SECOND).toFixed(1));
  }

  /**
   * Merge new search-mode results (keyword or AI) into an existing highlights
   * array: keeps everything that ISN'T a non-promoted search-* entry (promoted
   * ones survive as-is), then adds new results that don't collide with a
   * retained one within SEARCH_DUP_TOLERANCE_SECONDS. Shared by searchByKeywords
   * (in-memory), runAiSearch (in-memory), and persistSearchResultsToOriginalVod
   * (against a saved-state snapshot) — single place implementing the
   * "search-* entries survive rebuilds unless promoted" rule.
   */
  private mergeSearchHighlights(existing: Highlight[], incoming: Highlight[]): Highlight[] {
    const retained = existing.filter(h => {
      const isSearch = h.source === 'search-keyword' || h.source === 'search-ai';
      return !isSearch || !!h.isPromoted;
    });
    const tol = this.SEARCH_DUP_TOLERANCE_SECONDS;
    // Transcript segments («Речь») are long, dense and independent of chat — they must
    // not swallow chat-search hits that happen to start near them.
    const nonDupe = incoming.filter(nh =>
      !retained.some(r => !isTranscriptSource(r.source) && Math.abs(r.startTime - nh.startTime) < tol)
    );
    return [...retained, ...nonDupe].sort((a, b) => a.startTime - b.startTime);
  }

  /**
   * Keyword search across the loaded chat. Respects active chat filters (uses filteredComments).
   * Supports: space-separated AND tokens, "exact phrases", -excluded tokens.
   * Default applies Russian stemming so «мина» also matches «мину/мине/мины».
   *
   * Returns number of highlight moments produced. Populates:
   *  - state.highlights with new source='search-keyword' entries (drops previous non-promoted)
   *  - state.searchQuery, state.searchBinMatches (for heatmap overlay), state.searchHistory
   */
  searchByKeywords(rawQuery: string): number {
    const query = rawQuery.trim();
    if (!query) {
      this.clearSearchResults();
      return 0;
    }

    const parsed = parseSearchQuery(query);
    if (parsed.positive.length === 0 && parsed.phrases.length === 0) {
      this.state.error.set('Укажите хотя бы одно слово для поиска (не только исключения).');
      return 0;
    }

    const exactMatch = this.state.searchExactMatch();
    const comments = this.state.filteredComments();
    if (comments.length === 0) {
      this.state.error.set('Чат пуст или все сообщения отфильтрованы — искать не в чем.');
      return 0;
    }

    // «Область интереса»: only bins inside it are scanned — both the heatmap tint and the
    // result clusters stay within the region.
    const region = this.state.activeRegion();
    const binAccum = new Map<number, SearchBinAccum>();
    for (const comment of comments) {
      const binStart = Math.floor(comment.content_offset_seconds / this.BIN_DURATION_SECONDS) * this.BIN_DURATION_SECONDS;
      if (region && (binStart < region.inSec || binStart >= region.outSec)) continue;
      let bin = binAccum.get(binStart);
      if (!bin) {
        bin = { matchCount: 0, totalMessages: 0, samples: [] };
        binAccum.set(binStart, bin);
      }
      bin.totalMessages++;

      const match = matchText(comment.message.body, parsed, exactMatch);
      if (match.matched) {
        bin.matchCount++;
        if (bin.samples.length < this.SEARCH_MAX_SAMPLES) {
          bin.samples.push({
            text: `${comment.commenter.display_name}: ${comment.message.body}`,
            matchedKeywords: match.matchedKeywords,
          });
        }
      }
    }

    const binMatchCounts = new Map<number, number>();
    for (const [start, bin] of binAccum) {
      if (bin.matchCount > 0) binMatchCounts.set(start, bin.matchCount);
    }

    this.state.searchBinMatches.set(binMatchCounts);
    this.state.searchQuery.set(query);
    this.state.pushSearchHistory(query);
    this.state.error.set('');

    const clusters = this.clusterSearchBins(binAccum);
    const offset = this.state.rewindOffsetSeconds();
    // Flatten OR-groups for display/highlighting: any term from any group is a potential match worth marking.
    const displayKeywords = [...parsed.positive.flat(), ...parsed.phrases.map(p => `"${p}"`)];

    const newHighlights: Highlight[] = clusters.map(cluster => {
      const clipStart = Math.max(0, cluster.startTime - offset);
      const clipEnd = cluster.endTime + this.BIN_DURATION_SECONDS;
      const duration = clipEnd - clipStart;
      return {
        source: 'search-keyword',
        searchQuery: query,
        matchedKeywords: displayKeywords,
        matchCount: cluster.matchCount,
        messageCount: cluster.totalMessages,
        sampleMessages: cluster.samples.map(s => s.text),
        startTime: cluster.startTime,
        endTime: cluster.endTime,
        clipStartTime: clipStart,
        clipEndTime: clipEnd,
        estimatedSizeMb: this.calculateEstimatedSize(duration),
        timestamp: this.formatTime(cluster.startTime),
        sentiment: 'Нейтральный',
        description: `«${query}» — ${cluster.matchCount} ${cluster.matchCount === 1 ? 'совпадение' : 'совпадений'} в чате момента`,
        isAnalyzed: true,
        isPinned: false,
        isDeleted: false,
        isPromoted: false,
      };
    });

    this.state.highlights.update(current => this.mergeSearchHighlights(current, newHighlights));

    // Select first search result if there's no current selection in search tab
    const tab = this.state.activeTab();
    if (tab === 'search' && newHighlights.length > 0) {
      const sorted = this.state.sortedHighlights();
      if (sorted.length > 0 && !sorted.some(h => h.startTime === this.state.currentHighlight()?.startTime)) {
        this.selectHighlight(sorted[0].startTime);
      }
    }

    return newHighlights.length;
  }

  /** Remove non-promoted search results and clear search overlay state. */
  clearSearchResults(): void {
    this.state.highlights.update(current => current.filter(h => {
      const isSearch = h.source === 'search-keyword' || h.source === 'search-ai';
      return !isSearch || !!h.isPromoted;
    }));
    this.state.searchQuery.set('');
    this.state.searchBinMatches.set(new Map());
  }

  /**
   * Split a chat sample into char-level segments marking which characters are search matches.
   * Applies the same stemmer used during search, so «мину» gets highlighted when the query was «мина».
   * Phrases (wrapped in quotes) are matched by substring; everything else is stem-matched word-by-word.
   */
  highlightMatchesInSample(text: string, matchedKeywords: string[] | undefined): Array<{ text: string; isMatch: boolean }> {
    return highlightMatchSegments(text, matchedKeywords, this.state.searchExactMatch());
  }

  /** Toggle the isPromoted flag on a search result — makes it show up in ВСЕ tab. */
  togglePromoteHighlight(startTime: number): void {
    this.state.highlights.update(current => {
      const idx = current.findIndex(h => h.startTime === startTime);
      if (idx === -1) return current;
      const newHs = [...current];
      newHs[idx] = { ...newHs[idx], isPromoted: !newHs[idx].isPromoted };
      return newHs;
    });
  }

  private clusterSearchBins(binAccum: Map<number, SearchBinAccum>): SearchCluster[] {
    const matchingBins = Array.from(binAccum.entries())
      .filter(([_, b]) => b.matchCount > 0)
      .map(([startTime, b]) => ({ startTime, ...b }))
      .sort((a, b) => a.startTime - b.startTime);

    if (matchingBins.length === 0) return [];

    const matchingSet = new Set(matchingBins.map(b => b.startTime));
    const result: SearchCluster[] = [];
    let current: SearchCluster | null = null;

    for (const bin of matchingBins) {
      if (current) {
        const gap = bin.startTime - current.endTime;
        const isAdjacent = gap === this.BIN_DURATION_SECONDS;
        const isOneGap = gap === this.BIN_DURATION_SECONDS * 2
          && !matchingSet.has(current.endTime + this.BIN_DURATION_SECONDS);
        const prospectiveEnd = bin.startTime + this.BIN_DURATION_SECONDS;
        const wouldExceedMax = (prospectiveEnd - current.startTime) > this.SEARCH_MAX_WINDOW_SECONDS;

        if ((isAdjacent || isOneGap) && !wouldExceedMax) {
          if (isOneGap) {
            const bridge = binAccum.get(current.endTime + this.BIN_DURATION_SECONDS);
            if (bridge) current.totalMessages += bridge.totalMessages;
          }
          current.endTime = bin.startTime;
          current.matchCount += bin.matchCount;
          current.totalMessages += bin.totalMessages;
          for (const s of bin.samples) {
            if (current.samples.length < this.SEARCH_MAX_SAMPLES) current.samples.push(s);
          }
          continue;
        }
      }
      if (current) result.push(current);
      current = {
        startTime: bin.startTime,
        endTime: bin.startTime,
        matchCount: bin.matchCount,
        totalMessages: bin.totalMessages,
        samples: [...bin.samples],
      };
    }
    if (current) result.push(current);
    return result;
  }

  private updateHighlightsFromClusters(): void {
    const clusters = this.potentialClusters();
    const offset = this.state.rewindOffsetSeconds();
    const aiCache = this.state.analysisCache();
    const currentHighlights = this.state.highlights();

    // Cluster startTimes consumed by prior merges (transitive). Without filtering them
    // out, the original peaks would resurrect on every recalc and live alongside the
    // merged highlight as duplicates at the same / nearby startTime.
    const consumedByMerge = new Set<number>();
    for (const h of currentHighlights) {
      if (h.isMerged && h.mergedFromStartTimes?.length) {
        for (const t of h.mergedFromStartTimes) consumedByMerge.add(t);
      }
    }

    // 1. Generate new highlights based on current threshold
    const newPeakHighlights: Highlight[] = clusters
      .filter(cluster => !consumedByMerge.has(cluster.startTime))
      .map(cluster => {
      const cachedState = this.state.highlightStateCache.get(cluster.startTime);
      const cachedAiData = aiCache.get(cluster.startTime);

      // A start not set by hand follows Pre-Roll — caches saved before 2026-10-21 can still hold
      // the offset of the cluster's creation (updateRewindOffset didn't remap them then).
      const clipStart = cachedState?.isStartTimeManuallySet
        ? cachedState.clipStartTime
        : Math.max(0, cluster.startTime - offset);
      const clipEnd = Math.max(cachedState?.clipEndTime ?? cluster.endTime + this.BIN_DURATION_SECONDS, clipStart + 0.1);
      const duration = clipEnd - clipStart;

      const baseHighlight: Highlight = {
        source: 'peak',
        startTime: cluster.startTime,
        endTime: cluster.endTime,
        messageCount: cluster.messageCount,
        sampleMessages: cluster.messages.slice(0, 15),
        timestamp: this.formatTime(cluster.startTime),
        sentiment: 'Нейтральный',
        description: 'Нажмите "Анализировать моменты с ИИ", чтобы получить описание.',
        isAnalyzed: false,
        isPinned: cachedState?.isPinned ?? false,
        isDeleted: cachedState?.isDeleted ?? false,
        clipStartTime: clipStart,
        clipEndTime: clipEnd,
        estimatedSizeMb: this.calculateEstimatedSize(duration),
        isStartTimeManuallySet: cachedState?.isStartTimeManuallySet ?? false,
      };

      if (cachedAiData) {
        baseHighlight.description = cachedAiData.description;
        baseHighlight.sentiment = cachedAiData.sentiment;
        baseHighlight.isAnalyzed = true;
      }

      if (!cachedState) {
        this.state.highlightStateCache.set(cluster.startTime, {
          isPinned: baseHighlight.isPinned,
          isDeleted: baseHighlight.isDeleted,
          clipStartTime: baseHighlight.clipStartTime,
          clipEndTime: baseHighlight.clipEndTime,
          isStartTimeManuallySet: baseHighlight.isStartTimeManuallySet,
        });
      }

      return baseHighlight;
    });

    // 2. Preserve special-case peak highlights that may not have a backing cluster after
    // a recalc (filter change, threshold raise) — pinned peaks (user explicitly kept them)
    // and merged peaks (no real cluster ever; consumed startTimes were filtered above —
    // isMerged is the marker that keeps the highlight alive). AI deep-dive / stream-marker
    // / search are handled separately below. currentHighlights is reused from above.
    const preservedPeaks = currentHighlights.filter(h =>
      h.source === 'peak' && (h.isPinned || h.isMerged)
    );

    for (const p of preservedPeaks) {
      // Check if this preserved highlight is already present in the new list (by start time)
      const exists = newPeakHighlights.some(h => Math.abs(h.startTime - p.startTime) < 0.1);
      if (!exists) {
        newPeakHighlights.push(p);
      }
    }

    // Also preserve Deleted items? Usually if threshold raises, items disappear anyway. 
    // But if "Deleted" items are not in the new list, they are gone from "Trash" too, which is fine.
    // However, user specifically asked to keep Pinned.

    const existingAiHighlights = currentHighlights.filter(h => h.source === 'ai-deep-dive');
    // Pull stream-markers from the live highlights[] (same pattern as AI/search) instead
    // of state.streamMarkers signal. The signal isn't part of SavedState — on page refresh
    // it's empty until fetchAndMergeStreamMarkers re-runs async; cranking the threshold
    // before that resolves would otherwise wipe markers off the list (they're still in
    // highlights[] from the loaded SavedState, but we ignored that source).
    const existingStreamMarkers = currentHighlights.filter(h => h.source === 'stream-marker');
    // Preserve search highlights across threshold/filter changes — treat like AI-generated results.
    // Bins they point to may be stale after filter change, but sample messages stay valid.
    const existingSearchHighlights = currentHighlights.filter(h =>
      h.source === 'search-keyword' || h.source === 'search-ai'
    );
    // Preserve user-clips (Helix-fetched) across rebuilds. Same logic as stream-markers:
    // signal isn't in SavedState, on a fresh load they're empty until fetchAndMergeUserClips
    // re-runs async — pulling from highlights[] keeps them alive across threshold changes.
    const existingUserClips = currentHighlights.filter(h => h.source === 'user-clip');
    // «Речь» (admin-only): Gemini segments / AI-search over the transcript — chat-independent.
    const existingTranscriptHighlights = currentHighlights.filter(h => isTranscriptSource(h.source));

    const combinedHighlights = [
      ...existingAiHighlights,
      ...existingStreamMarkers,
      ...existingSearchHighlights,
      ...existingUserClips,
      ...existingTranscriptHighlights,
      ...newPeakHighlights,
    ].sort((a, b) => a.startTime - b.startTime);
    this.state.highlights.set(combinedHighlights);

    const hasUnanalyzedPeaks = combinedHighlights.some(h => !h.isAnalyzed && h.source === 'peak');
    this.state.isAnalyzed.set(!hasUnanalyzedPeaks);

    this.state.selectedToMerge.update(currentSet => {
      const existingStartTimes = new Set(combinedHighlights.map(h => h.startTime));
      const newSet = new Set<number>();
      for (const id of currentSet) {
        if (existingStartTimes.has(id)) newSet.add(id);
      }
      return newSet;
    });

    const currentHighlightStartTime = this.state.currentHighlight()?.startTime;
    const currentHighlightStillExists = combinedHighlights.some(h => h.startTime === currentHighlightStartTime);
    if (!currentHighlightStillExists) {
      if (this.state.sortedHighlights().length > 0) {
        this.selectHighlight(this.state.sortedHighlights()[0].startTime);
      } else {
        const firstAvailable = this.state.highlights().find(h => !h.isDeleted);
        if (firstAvailable) this.selectHighlight(firstAvailable.startTime);
        else this.state.currentHighlightIndex.set(0);
      }
    }
  }

  async analyzeHighlightsWithAI(): Promise<void> {
    if (checkDemoMode()) {
      const { DemoService } = await import('./demo.service.js');
      return this.injector.get(DemoService).simulateQuickAi();
    }
    const region = this.state.activeRegion();
    let highlightsToAnalyze = this.state.highlights().filter(h =>
      !h.isAnalyzed && h.source === 'peak' && (!region || overlapsRegion(h.startTime, h.endTime, region)));

    // While chat is still downloading, drop the latest cluster from this run — its
    // bins may absorb more messages on the next batch (cluster algorithm has 1-bin
    // gap tolerance, so the cluster could extend forward). The next Quick run after
    // the download front advances past it (or after completion) will pick it up.
    // The component gates the Quick button by analyzableQuickCount which already
    // accounts for this exclusion, so the user only clicks when there's something
    // safe behind the front.
    if (this.state.isChatDownloading() && highlightsToAnalyze.length > 0) {
      const boundary = this.state.quickBoundaryHighlight();
      if (boundary) {
        highlightsToAnalyze = highlightsToAnalyze.filter(h => h.startTime !== boundary.startTime);
      }
    }

    if (highlightsToAnalyze.length === 0) return;

    // Capture VOD identity + context up front. Used as Gemini input AND as a fence
    // so results can be routed to the right VOD even if the user switches streams
    // while the call is in flight.
    const originalVideoId = this.state.videoId();
    const context = {
      streamer: this.state.streamerName() || 'Неизвестный',
      title: this.state.videoTitle() || 'Без названия',
      game: this.state.videoGame() || 'Неизвестная игра',
    };

    this.state.isAnalyzingQuick.set(true);
    this.state.error.set('');

    try {
      await new Promise(resolve => setTimeout(resolve, 50));

      const clustersToAnalyze = highlightsToAnalyze.map(highlight => ({
        startTime: highlight.startTime,
        messages: highlight.sampleMessages.map(m => m.split(': ').slice(1).join(': '))
      }));

      const aiResults = await this.geminiService.analyzeMultipleChatHighlights(
        clustersToAnalyze,
        context,
        (info) => this.state.quickRetryInfo.set(info)
      );

      // Resolve each AI result to the closest analyzed highlight's startTime.
      const aiCacheUpdates = new Map<number, AnalysisCacheData>();
      for (const result of aiResults) {
        let bestMatch = highlightsToAnalyze[0];
        let minDiff = Infinity;
        for (const h of highlightsToAnalyze) {
          const diff = Math.abs(h.startTime - result.startTime);
          if (diff < minDiff) {
            minDiff = diff;
            bestMatch = h;
          }
        }
        const resolvedTime = (bestMatch && minDiff < 60) ? bestMatch.startTime : result.startTime;
        aiCacheUpdates.set(resolvedTime, {
          description: result.description,
          sentiment: result.sentiment,
        });
      }

      if (this.state.videoId() === originalVideoId) {
        // Same VOD still loaded — apply via in-memory signals.
        const updatedCache = new Map(this.state.analysisCache());
        for (const [k, v] of aiCacheUpdates) updatedCache.set(k, v);
        this.state.analysisCache.set(updatedCache);

        this.state.highlights.update(currentHighlights => currentHighlights.map(h => {
          const cached = aiCacheUpdates.get(h.startTime);
          if (cached && h.source === 'peak') {
            return { ...h, description: cached.description, sentiment: cached.sentiment, isAnalyzed: true };
          }
          return h;
        }));

        this.state.isAnalyzed.set(true);
      } else if (originalVideoId != null) {
        // VOD was swapped while AI was running — write results to the original
        // VOD's saved state so they're visible next time it's opened.
        await this.persistAiResultsToOriginalVod(originalVideoId, aiCacheUpdates, []);
      }

    } catch (e) {
      // Daily-quota 429 (server beat the client-side pre-check, e.g. on a
      // race or stale UI). Surface a Russian explanation instead of the raw
      // 'QUOTA_EXCEEDED' string.
      if (e instanceof QuotaExceededError) {
        const reset = e.resetAtIso ? new Date(e.resetAtIso).toUTCString() : 'полночь UTC';
        if (this.state.videoId() === originalVideoId) {
          this.state.error.set(`Дневной лимит AI-анализа исчерпан. Сбросится в ${reset}.`);
        } else {
          console.warn('[AI background] Quick analysis quota exceeded for VOD', originalVideoId);
        }
      } else {
        const message = e instanceof Error ? e.message : 'Произошла неизвестная ошибка.';
        if (this.state.videoId() === originalVideoId) {
          this.state.error.set(`Ошибка при анализе моментов: ${message}`);
        } else {
          console.error('[AI background] Quick analysis failed for VOD', originalVideoId, message);
        }
      }
    } finally {
      this.state.isAnalyzingQuick.set(false);
      this.state.quickRetryInfo.set(null);
    }
  }

  /**
   * Bins for a Deep / AI-search run. With an active «область интереса» — only its bins,
   * rebased so the region starts at 0: both servers derive the stream length (cap,
   * per-hour sweep, «Длительность стрима») from the max bin startTime, so absolute
   * times would describe a 30-min window at 5:00 as a 6-hour stream. `base` (a multiple
   * of 15) is added back onto the results. null = the region holds no chat at all —
   * caller shows a neutral message instead of letting the server answer CHAT_EMPTY
   * («чат не загружен»), which would be misleading here.
   */
  private regionScopedBins(bins: ActivityBin[]): { bins: ActivityBin[]; region: TimeRegion | null; base: number } | null {
    const region = this.state.activeRegion();
    if (!region) return { bins, region: null, base: 0 };
    const inside = binsInRegion(bins, region);
    if (!inside.some(b => b.messageCount > 0)) return null;
    return { bins: rebaseBins(inside, region.inSec), region, base: region.inSec };
  }

  /** Shift region-run results back to absolute VOD time and drop anything outside the region. */
  private unrebaseRegionResults<T extends { startTime: number; endTime: number }>(
    results: T[],
    region: TimeRegion | null,
    base: number,
  ): T[] {
    if (!region) return results;
    return results
      .map(r => ({ ...r, startTime: r.startTime + base, endTime: r.endTime + base }))
      .filter(r => overlapsRegion(r.startTime, r.endTime, region));
  }

  /**
   * The open VOD's streamer login for AI Deep — viewers mention the streamer by login, which a
   * localized display name doesn't match. Known from the download; for a VOD restored from saved
   * state, the library channel (project → channel, else by name — same lookup as the clips fallback).
   */
  private streamerLoginForAi(): string | undefined {
    const known = this.state.streamerLogin();
    if (known) return known;
    const channels = this.libraryService.savedChannels();
    const vid = this.state.videoId();
    const channelId = vid != null ? this.libraryService.getProject(String(vid))?.channelId : undefined;
    const byProject = channelId ? channels.find(c => c.id === channelId) : undefined;
    if (byProject) return byProject.login;
    const name = this.state.streamerName()?.toLowerCase();
    return name ? channels.find(c => c.login === name || c.name.toLowerCase() === name)?.login : undefined;
  }

  async runDeepAnalysis(): Promise<void> {
    if (checkDemoMode()) {
      const { DemoService } = await import('./demo.service.js');
      return this.injector.get(DemoService).simulateDeepAi();
    }
    const bins = this.state.activityBins();
    if (bins.length === 0) return;
    const scope = this.regionScopedBins(bins);
    if (!scope) {
      this.state.infoMessage.set('В выбранной области нет сообщений чата — глубокому поиску нечего читать.');
      return;
    }
    const { region, base } = scope;

    // Capture VOD identity + the inputs needed to build new highlights — bins,
    // rewind offset — so a mid-flight VOD switch routes results to the original
    // VOD instead of corrupting the new one.
    const originalVideoId = this.state.videoId();
    const rewindOffset = this.state.rewindOffsetSeconds();
    const streamerLogin = this.streamerLoginForAi();
    const context = {
      streamer: this.state.streamerName() || 'Неизвестный',
      ...(streamerLogin ? { streamerLogin } : {}),
      title: this.state.videoTitle() || 'Без названия',
      game: this.state.videoGame() || 'Неизвестная игра',
    };

    // Tell the server which time ranges have already been covered so re-runs
    // search for OTHER moments instead of redundantly resuggesting the same
    // events. Includes:
    //   - all ai-deep-dive (including isDeleted — the user already saw and
    //     either kept or rejected them; either way, no need to resurface);
    //   - peak highlights with isAnalyzed=true (Quick already described them).
    // Peak highlights without descriptions are NOT excluded — Deep may pick
    // them up with a different lens than Quick.
    // With a region: only ranges touching it, shifted into the rebased timeline.
    const excludeRanges = this.state.highlights()
      .filter(h => h.source === 'ai-deep-dive' || (h.source === 'peak' && h.isAnalyzed))
      .filter(h => !region || overlapsRegion(h.startTime, h.endTime, region))
      .map(h => ({ startTime: Math.max(0, h.startTime - base), endTime: Math.max(0, h.endTime - base) }));

    this.state.isAnalyzingDeep.set(true);
    this.state.error.set('');
    this.state.infoMessage.set('');

    try {
      await new Promise(resolve => setTimeout(resolve, 50));
      const aiResults = this.unrebaseRegionResults(await this.geminiService.performDeepChatAnalysis(
        scope.bins,
        context,
        (info) => this.state.deepRetryInfo.set(info),
        excludeRanges,
      ), region, base);

      // Empty is now a LEGITIMATE outcome, not a malfunction: the deep prompt
      // explicitly permits returning [] when the stream has no identifiable
      // bright events (quiet / low-traffic streams), and the server also drops
      // low-confidence moments. So we NEVER throw on empty — surface a neutral
      // blue info banner instead of an alarming red error. Wording differs only
      // by first-run vs excludeRanges re-run.
      if (aiResults.length === 0) {
        if (this.state.videoId() === originalVideoId) {
          this.state.infoMessage.set(region
            ? (excludeRanges.length > 0
                ? 'Глубокий анализ: новых моментов в выбранной области не найдено — она уже проанализирована.'
                : 'Глубокий анализ завершён: ярких моментов в выбранной области не найдено.')
            : (excludeRanges.length > 0
                ? 'Глубокий анализ: новых моментов не найдено — стрим уже проанализирован.'
                : 'Глубокий анализ завершён: ярких моментов на этом стриме не найдено.')
          );
        } else {
          console.info('[AI background] Deep on VOD', originalVideoId, 'returned no moments (empty is a valid result)');
        }
        return;
      }

      const aiCacheUpdates = new Map<number, AnalysisCacheData>();
      for (const result of aiResults) {
        aiCacheUpdates.set(result.startTime, {
          description: result.description,
          sentiment: result.sentiment,
        });
      }

      const allBinsMap = new Map(bins.map(b => [b.startTime, b]));
      const aiGeneratedHighlights: Highlight[] = aiResults.map((result: HighlightDeepAnalysisResponse) => {
        let messageCount = 0;
        const sampleMessages: string[] = [];
        for (let t = result.startTime; t <= result.endTime; t += this.BIN_DURATION_SECONDS) {
          const bin = allBinsMap.get(t);
          if (bin) {
            messageCount += bin.messageCount;
            if (sampleMessages.length < 20) {
              sampleMessages.push(...bin.messages);
            }
          }
        }

        const clipStart = Math.max(0, result.startTime - rewindOffset);
        const clipEnd = result.endTime + this.BIN_DURATION_SECONDS;
        const duration = clipEnd - clipStart;

        return {
          source: 'ai-deep-dive',
          startTime: result.startTime,
          endTime: result.endTime,
          messageCount: messageCount,
          sampleMessages: sampleMessages.slice(0, 20),
          clipStartTime: clipStart,
          clipEndTime: clipEnd,
          estimatedSizeMb: this.calculateEstimatedSize(duration),
          timestamp: this.formatTime(result.startTime),
          sentiment: result.sentiment,
          description: result.description,
          isAnalyzed: true,
          isPinned: false,
          isDeleted: false,
        };
      });

      if (this.state.videoId() === originalVideoId) {
        // Same VOD still loaded — apply via in-memory signals.
        this.state.analysisCache.update(cache => {
          for (const [k, v] of aiCacheUpdates) cache.set(k, v);
          return new Map(cache);
        });

        // Preserve user's current selection across the array re-sort below.
        // currentHighlightIndex is an index into highlights[], and sort() changes positions —
        // so we save the startTime (stable key) and re-resolve the index after the update.
        const previousSelectedStartTime = this.state.currentHighlight()?.startTime;

        this.state.highlights.update(currentHighlights => {
          const updatedHighlights = [...currentHighlights];
          const updatedHighlightIndices = new Set<number>();

          for (const aiHighlight of aiGeneratedHighlights) {
            const existingHighlightIndex = updatedHighlights.findIndex((h, i) =>
              !updatedHighlightIndices.has(i) &&
              (h.source === 'peak') &&
              Math.abs(h.startTime - aiHighlight.startTime) < 30
            );

            if (existingHighlightIndex > -1) {
              const originalHighlight = updatedHighlights[existingHighlightIndex];
              const cachedData = this.state.analysisCache().get(aiHighlight.startTime);
              if (cachedData) {
                updatedHighlights[existingHighlightIndex] = {
                  ...originalHighlight,
                  description: cachedData.description,
                  sentiment: cachedData.sentiment,
                  isAnalyzed: true,
                };
                updatedHighlightIndices.add(existingHighlightIndex);
              }
            } else {
              const isDuplicateAiHighlight = updatedHighlights.some(h =>
                h.source === 'ai-deep-dive' && Math.abs(h.startTime - aiHighlight.startTime) < 30
              );
              if (!isDuplicateAiHighlight) {
                updatedHighlights.push(aiHighlight);
              }
            }
          }

          return updatedHighlights.sort((a, b) => a.startTime - b.startTime);
        });

        if (previousSelectedStartTime !== undefined) {
          const newIndex = this.state.highlights().findIndex(h => h.startTime === previousSelectedStartTime);
          if (newIndex >= 0) {
            this.state.currentHighlightIndex.set(newIndex);
          }
        }
      } else if (originalVideoId != null) {
        // VOD was swapped — write results to the original VOD's saved state.
        await this.persistAiResultsToOriginalVod(originalVideoId, aiCacheUpdates, aiGeneratedHighlights);
      }

    } catch (e) {
      if (e instanceof QuotaExceededError) {
        const reset = e.resetAtIso ? new Date(e.resetAtIso).toUTCString() : 'полночь UTC';
        if (this.state.videoId() === originalVideoId) {
          this.state.error.set(`Дневной лимит глубокого AI-поиска исчерпан. Сбросится в ${reset}.`);
        } else {
          console.warn('[AI background] Deep analysis quota exceeded for VOD', originalVideoId);
        }
      } else {
        const message = e instanceof Error ? e.message : 'Произошла неизвестная ошибка.';
        if (this.state.videoId() === originalVideoId) {
          this.state.error.set(`Ошибка при глубоком анализе: ${message}`);
        } else {
          console.error('[AI background] Deep analysis failed for VOD', originalVideoId, message);
        }
      }
    } finally {
      this.state.isAnalyzingDeep.set(false);
      this.state.deepRetryInfo.set(null);
    }
  }

  /**
   * Persist AI-analysis results to a VOD's saved state when that VOD is no longer
   * the one currently loaded — happens when the user switches streams during an
   * AI call. Reads the latest IDB snapshot, merges in cache + highlight updates,
   * writes back, and (for Pro) pushes via SyncService.
   */
  private async persistAiResultsToOriginalVod(
    videoId: string | number,
    aiCacheUpdates: Map<number, AnalysisCacheData>,
    aiNewDeepHighlights: Highlight[],
  ): Promise<void> {
    try {
      const saved: SavedState | undefined = await this.libraryService.getProjectState(String(videoId));
      if (!saved) {
        console.warn('[AI background] saved state for VOD', videoId, 'not found, skipping persistence');
        return;
      }

      const mergedCacheMap = new Map<number, AnalysisCacheData>(saved.analysisCache || []);
      for (const [k, v] of aiCacheUpdates) mergedCacheMap.set(k, v);

      // Apply description/sentiment to existing peak highlights.
      const updatedHighlights: Highlight[] = (saved.highlights || []).map(h => {
        const cached = aiCacheUpdates.get(h.startTime);
        if (cached && h.source === 'peak') {
          return { ...h, description: cached.description, sentiment: cached.sentiment, isAnalyzed: true };
        }
        return h;
      });

      // Append new ai-deep-dive items (Deep only). Same dedup as the in-memory path:
      // if a peak within ±30s exists, attach the description there; otherwise add
      // unless another ai-deep-dive within ±30s already exists.
      for (const newH of aiNewDeepHighlights) {
        const peakIdx = updatedHighlights.findIndex(h =>
          h.source === 'peak' && Math.abs(h.startTime - newH.startTime) < 30
        );
        if (peakIdx > -1) {
          updatedHighlights[peakIdx] = {
            ...updatedHighlights[peakIdx],
            description: newH.description,
            sentiment: newH.sentiment,
            isAnalyzed: true,
          };
          continue;
        }
        const isDuplicate = updatedHighlights.some(h =>
          h.source === 'ai-deep-dive' && Math.abs(h.startTime - newH.startTime) < 30
        );
        if (!isDuplicate) updatedHighlights.push(newH);
      }

      updatedHighlights.sort((a, b) => a.startTime - b.startTime);

      const updatedState: SavedState = {
        ...saved,
        highlights: updatedHighlights,
        analysisCache: Array.from(mergedCacheMap.entries()),
        isAnalyzed: true,
        syncDirty: true,
      };

      await this.libraryService.saveProjectState(String(videoId), updatedState);
      // Pro users: keep cloud copy in sync. No-op for free users.
      this.syncService.pushProjectState(String(videoId), updatedState);

      console.info('[AI background] persisted results for VOD', videoId);
    } catch (e) {
      console.error('[AI background] failed to persist results for VOD', videoId, e);
    }
  }

  /**
   * Query-driven AI search: user describes a moment in free text, Gemini reads
   * the whole chat (same ActivityBin sampling as Deep) and returns candidates
   * with a 0-100 confidence score. Populates state.highlights with new
   * source='search-ai' entries via mergeSearchHighlights (same rule keyword
   * search uses — drops previous non-promoted search-* results, keeps promoted
   * ones). Empty result is a legitimate outcome (off-topic query or no match in
   * this chat) — never thrown as an error, surfaced as a neutral infoMessage.
   */
  async runAiSearch(query: string): Promise<void> {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    const bins = this.state.activityBins();
    if (bins.length === 0) return;
    const scope = this.regionScopedBins(bins);
    if (!scope) {
      this.state.infoMessage.set('В выбранной области нет сообщений чата — искать не в чем.');
      return;
    }
    const { region, base } = scope;

    // Capture VOD identity + inputs needed to build highlights so a mid-flight
    // VOD switch routes results to the original VOD instead of corrupting the
    // new one (same pattern as analyzeHighlightsWithAI / runDeepAnalysis).
    const originalVideoId = this.state.videoId();
    const rewindOffset = this.state.rewindOffsetSeconds();
    const context = {
      streamer: this.state.streamerName() || 'Неизвестный',
      title: this.state.videoTitle() || 'Без названия',
      game: this.state.videoGame() || 'Неизвестная игра',
    };

    this.state.isAnalyzingSearch.set(true);
    this.state.error.set('');
    this.state.infoMessage.set('');

    try {
      await new Promise(resolve => setTimeout(resolve, 50));
      const aiResults = this.unrebaseRegionResults(await this.geminiService.performSearchAnalysis(
        scope.bins,
        context,
        trimmedQuery,
        (info) => this.state.searchRetryInfo.set(info),
      ), region, base);

      this.state.pushSearchHistory(trimmedQuery);

      if (aiResults.length === 0) {
        if (this.state.videoId() === originalVideoId) {
          this.state.infoMessage.set(region ? 'По этому запросу в выбранной области ничего не нашлось.' : 'По этому запросу ничего не нашлось.');
        } else {
          console.info('[AI background] Search on VOD', originalVideoId, 'returned no moments (empty is a valid result)');
        }
        return;
      }

      const allBinsMap = new Map(bins.map(b => [b.startTime, b]));
      const newHighlights: Highlight[] = aiResults.map((result: HighlightSearchResponse) => {
        let messageCount = 0;
        const sampleMessages: string[] = [];
        for (let t = result.startTime; t <= result.endTime; t += this.BIN_DURATION_SECONDS) {
          const bin = allBinsMap.get(t);
          if (bin) {
            messageCount += bin.messageCount;
            if (sampleMessages.length < 20) {
              sampleMessages.push(...bin.messages);
            }
          }
        }

        const clipStart = Math.max(0, result.startTime - rewindOffset);
        const clipEnd = result.endTime + this.BIN_DURATION_SECONDS;
        const duration = clipEnd - clipStart;

        return {
          source: 'search-ai',
          searchQuery: trimmedQuery,
          confidence: result.confidence,
          startTime: result.startTime,
          endTime: result.endTime,
          messageCount,
          sampleMessages: sampleMessages.slice(0, 20),
          clipStartTime: clipStart,
          clipEndTime: clipEnd,
          estimatedSizeMb: this.calculateEstimatedSize(duration),
          timestamp: this.formatTime(result.startTime),
          sentiment: 'Нейтральный',
          description: result.description,
          isAnalyzed: true,
          isPinned: false,
          isDeleted: false,
          isPromoted: false,
        };
      });

      if (this.state.videoId() === originalVideoId) {
        const before = this.state.highlights();
        this.state.highlights.update(current => this.mergeSearchHighlights(current, newHighlights));
        this.state.onDocumentEvent?.({
          kind: 'results-replaced', videoId: String(originalVideoId), group: 'search',
          scope: null, before, after: this.state.highlights(),
        });

        // Select first result if there's no current selection in the ПОИСК tab
        // (mirrors searchByKeywords' tail behavior).
        if (this.state.activeTab() === 'search') {
          const sorted = this.state.sortedHighlights();
          if (sorted.length > 0 && !sorted.some(h => h.startTime === this.state.currentHighlight()?.startTime)) {
            this.selectHighlight(sorted[0].startTime);
          }
        }
      } else if (originalVideoId != null) {
        await this.persistSearchResultsToOriginalVod(originalVideoId, newHighlights);
      }

    } catch (e) {
      if (e instanceof QuotaExceededError) {
        const reset = e.resetAtIso ? new Date(e.resetAtIso).toUTCString() : 'полночь UTC';
        if (this.state.videoId() === originalVideoId) {
          this.state.error.set(`Дневной лимит ИИ-поиска исчерпан. Сбросится в ${reset}.`);
        } else {
          console.warn('[AI background] Search quota exceeded for VOD', originalVideoId);
        }
      } else {
        const message = e instanceof Error ? e.message : 'Произошла неизвестная ошибка.';
        if (this.state.videoId() === originalVideoId) {
          this.state.error.set(`Ошибка при ИИ-поиске: ${message}`);
        } else {
          console.error('[AI background] Search failed for VOD', originalVideoId, message);
        }
      }
    } finally {
      this.state.isAnalyzingSearch.set(false);
      this.state.searchRetryInfo.set(null);
    }
  }

  /**
   * Persist AI-search results to a VOD's saved state when that VOD is no
   * longer the one currently loaded (user switched streams during the call).
   * Simplified sibling of persistAiResultsToOriginalVod — search-ai results
   * are opt-in (only visible in ВСЕ when isPromoted), never merged into peak
   * highlights, so this just applies mergeSearchHighlights against the saved
   * snapshot instead of the live signal.
   */
  private async persistSearchResultsToOriginalVod(
    videoId: string | number,
    newHighlights: Highlight[],
  ): Promise<void> {
    try {
      const saved: SavedState | undefined = await this.libraryService.getProjectState(String(videoId));
      if (!saved) {
        console.warn('[AI background] saved state for VOD', videoId, 'not found, skipping persistence');
        return;
      }

      const updatedHighlights = this.mergeSearchHighlights(saved.highlights || [], newHighlights);
      const updatedState: SavedState = { ...saved, highlights: updatedHighlights, syncDirty: true };

      await this.libraryService.saveProjectState(String(videoId), updatedState);
      // Pro users: keep cloud copy in sync. No-op for free users.
      this.syncService.pushProjectState(String(videoId), updatedState);
      this.state.onDocumentEvent?.({
        kind: 'results-replaced', videoId: String(videoId), group: 'search',
        scope: null, before: saved.highlights || [], after: updatedHighlights,
      });

      console.info('[AI background] persisted search results for VOD', videoId);
    } catch (e) {
      console.error('[AI background] failed to persist search results for VOD', videoId, e);
    }
  }

  selectHighlight(startTime: number): void {
    const highlights = this.state.highlights();
    const index = highlights.findIndex(h => h.startTime === startTime);

    if (index === -1 || this.state.currentHighlightIndex() === index) {
      return;
    }
    this.state.currentHighlightIndex.set(index);
  }

  softDeleteHighlight(startTimeToDelete: number): void {
    const displayIndex = this.state.sortedHighlights().findIndex(h => h.startTime === startTimeToDelete);

    this.state.highlights.update(currentHighlights => {
      const indexToUpdate = currentHighlights.findIndex(h => h.startTime === startTimeToDelete);
      if (indexToUpdate === -1) return currentHighlights;

      const newHighlights = [...currentHighlights];
      const updatedHighlight = { ...newHighlights[indexToUpdate], isDeleted: true, isPinned: false };
      newHighlights[indexToUpdate] = updatedHighlight;

      const currentState = this.state.highlightStateCache.get(startTimeToDelete);
      this.state.highlightStateCache.set(startTimeToDelete, {
        isPinned: false,
        isDeleted: true,
        clipStartTime: currentState?.clipStartTime ?? updatedHighlight.clipStartTime,
        clipEndTime: currentState?.clipEndTime ?? updatedHighlight.clipEndTime,
        isStartTimeManuallySet: currentState?.isStartTimeManuallySet ?? updatedHighlight.isStartTimeManuallySet,
      });

      return newHighlights;
    });

    if (this.state.selectedToMerge().has(startTimeToDelete)) {
      this.state.selectedToMerge.update(s => { const next = new Set(s); next.delete(startTimeToDelete); return next; });
    }

    const currentSelectedStartTime = this.state.currentHighlight()?.startTime;
    if (startTimeToDelete === currentSelectedStartTime) {
      const newVisibleHighlights = this.state.sortedHighlights();
      if (newVisibleHighlights.length > 0) {
        const newIndexToSelect = Math.min(displayIndex, newVisibleHighlights.length - 1);
        this.selectHighlight(newVisibleHighlights[newIndexToSelect].startTime);
      }
    }
  }

  restoreHighlight(startTimeToRestore: number): void {
    this.state.highlights.update(currentHighlights => {
      const indexToUpdate = currentHighlights.findIndex(h => h.startTime === startTimeToRestore);
      if (indexToUpdate === -1) return currentHighlights;

      const newHighlights = [...currentHighlights];
      const updatedHighlight = { ...newHighlights[indexToUpdate], isDeleted: false };
      newHighlights[indexToUpdate] = updatedHighlight;

      const currentState = this.state.highlightStateCache.get(startTimeToRestore);
      this.state.highlightStateCache.set(startTimeToRestore, {
        isPinned: currentState?.isPinned ?? updatedHighlight.isPinned,
        isDeleted: false,
        clipStartTime: currentState?.clipStartTime ?? updatedHighlight.clipStartTime,
        clipEndTime: currentState?.clipEndTime ?? updatedHighlight.clipEndTime,
        isStartTimeManuallySet: currentState?.isStartTimeManuallySet ?? updatedHighlight.isStartTimeManuallySet,
      });

      return newHighlights;
    });
  }

  deleteHighlightPermanently(startTimeToDelete: number): void {
    const displayIndex = this.state.sortedHighlights().findIndex(h => h.startTime === startTimeToDelete);
    const currentSelectedStartTime = this.state.currentHighlight()?.startTime;

    const newHighlights = this.state.highlights().filter(h => h.startTime !== startTimeToDelete);
    this.state.highlights.set(newHighlights);
    this.state.highlightStateCache.delete(startTimeToDelete);

    if (newHighlights.length === 0) {
      this.state.currentHighlightIndex.set(0);
      return;
    }

    if (startTimeToDelete === currentSelectedStartTime) {
      const newVisibleHighlights = this.state.sortedHighlights();
      if (newVisibleHighlights.length > 0) {
        const newIndexToSelect = Math.min(displayIndex, newVisibleHighlights.length - 1);
        this.selectHighlight(newVisibleHighlights[newIndexToSelect].startTime);
      }
    }
  }

  togglePinHighlight(startTime: number): void {
    this.state.highlights.update(currentHighlights => {
      const index = currentHighlights.findIndex(h => h.startTime === startTime);
      if (index === -1) return currentHighlights;

      const newHighlights = [...currentHighlights];
      const highlight = newHighlights[index];
      const newPinnedState = !highlight.isPinned;
      const updatedHighlight = { ...highlight, isPinned: newPinnedState };
      newHighlights[index] = updatedHighlight;

      const currentState = this.state.highlightStateCache.get(startTime);
      this.state.highlightStateCache.set(startTime, {
        isPinned: newPinnedState,
        isDeleted: currentState?.isDeleted ?? updatedHighlight.isDeleted,
        clipStartTime: currentState?.clipStartTime ?? updatedHighlight.clipStartTime,
        clipEndTime: currentState?.clipEndTime ?? updatedHighlight.clipEndTime,
        isStartTimeManuallySet: currentState?.isStartTimeManuallySet ?? updatedHighlight.isStartTimeManuallySet,
      });

      return newHighlights;
    });
  }

  setClipTimes(index: number, startTime: number, endTime: number): void {
    this.state.highlights.update(currentHighlights => {
      const newHighlights = [...currentHighlights];
      const oldHighlight = newHighlights[index];

      if (!oldHighlight) return currentHighlights;

      const newStartTime = Math.max(0, startTime);
      const newEndTime = Math.max(newStartTime + 0.1, endTime);
      const duration = newEndTime - newStartTime;

      const updatedHighlight = {
        ...oldHighlight,
        clipStartTime: newStartTime,
        clipEndTime: newEndTime,
        estimatedSizeMb: this.calculateEstimatedSize(duration),
        isStartTimeManuallySet: true,
      };
      newHighlights[index] = updatedHighlight;

      const currentState = this.state.highlightStateCache.get(updatedHighlight.startTime);
      this.state.highlightStateCache.set(updatedHighlight.startTime, {
        isPinned: currentState?.isPinned ?? updatedHighlight.isPinned,
        isDeleted: currentState?.isDeleted ?? updatedHighlight.isDeleted,
        clipStartTime: newStartTime,
        clipEndTime: newEndTime,
        isStartTimeManuallySet: true,
      });

      return newHighlights;
    });
  }

  mergeSelectedHighlights(): void {
    const selectedIds = this.state.selectedToMerge();
    if (selectedIds.size < 2) return;

    // Aggregate from highlights, NOT from potentialClusters — clusters only contain peak
    // sources, so a cluster-only path silently no-op'd whenever the user mixed a peak
    // with an AI-deep-dive or stream-marker. Highlights have everything: source,
    // sampleMessages, times, clip-extension state, and prior merge ancestry.
    const toMerge = this.state.highlights()
      .filter(h => selectedIds.has(h.startTime))
      .sort((a, b) => a.startTime - b.startTime);

    if (toMerge.length < 2) return;

    const firstHighlight = toMerge[0];
    const lastHighlight = toMerge[toMerge.length - 1];

    let mergedMessageCount = 0;
    const mergedMessages: string[] = [];
    // Transitive consumed startTimes — if any input was itself a merge result, inherit
    // its mergedFromStartTimes so the cluster-skip filter still hides the deepest
    // ancestors after a re-merge. Without transitivity, re-merging would lose track of
    // earlier consumed clusters and they'd resurrect on the next recalc.
    const mergedFromStartTimes: number[] = [];
    for (const h of toMerge) {
      mergedMessageCount += h.messageCount || 0;
      if (h.sampleMessages?.length) mergedMessages.push(...h.sampleMessages);
      if (h.mergedFromStartTimes?.length) {
        mergedFromStartTimes.push(...h.mergedFromStartTimes);
      } else {
        mergedFromStartTimes.push(h.startTime);
      }
    }

    const duration = lastHighlight.clipEndTime - firstHighlight.clipStartTime;

    // Always source='peak' so Quick AI stays available on the merged result. The
    // isMerged flag + mergedFromStartTimes drive preservation through cluster recalcs
    // (see updateHighlightsFromClusters). Description / sentiment are reset to a
    // placeholder + 'Смешанный' on every merge — Quick AI is the canonical way to
    // get a fresh description tuned to the merged scope. (Inheriting from an input
    // was tried and rejected as confusing — the inherited description rarely fits the
    // wider merged span, and isAnalyzed=false vs visible-text was inconsistent.)
    const newHighlight: Highlight = {
      source: 'peak',
      startTime: firstHighlight.startTime,
      endTime: lastHighlight.endTime,
      clipStartTime: firstHighlight.clipStartTime,
      clipEndTime: lastHighlight.clipEndTime,
      estimatedSizeMb: this.calculateEstimatedSize(duration),
      messageCount: mergedMessageCount,
      sampleMessages: mergedMessages.slice(0, 50),
      timestamp: this.formatTime(firstHighlight.startTime),
      sentiment: 'Смешанный',
      description: 'Объединенный момент. Нажмите "Анализировать моменты с ИИ", чтобы получить новое описание.',
      isStartTimeManuallySet: firstHighlight.isStartTimeManuallySet || lastHighlight.isStartTimeManuallySet,
      isAnalyzed: false,
      isPinned: false,
      isDeleted: false,
      isMerged: true,
      mergedFromStartTimes,
    };

    for (const id of selectedIds) {
      this.state.highlightStateCache.delete(id);
    }
    this.state.highlightStateCache.set(newHighlight.startTime, {
      isPinned: newHighlight.isPinned,
      isDeleted: newHighlight.isDeleted,
      clipStartTime: newHighlight.clipStartTime,
      clipEndTime: newHighlight.clipEndTime,
      isStartTimeManuallySet: newHighlight.isStartTimeManuallySet,
    });

    const remainingHighlights = this.state.highlights().filter(h => !selectedIds.has(h.startTime));
    const newHighlights = [...remainingHighlights, newHighlight].sort((a, b) => a.startTime - b.startTime);
    this.state.highlights.set(newHighlights);

    this.state.isAnalyzed.set(false);
    this.state.selectedToMerge.set(new Set());

    this.selectHighlight(newHighlight.startTime);
    this.selectHighlight(newHighlight.startTime);
  }

  // --- Stream Markers Integration ---

  private async fetchAndMergeStreamMarkers() {
    this.state.loadingMessage.set('Загрузка меток из Firebase...');

    // Read from state.streamCreatedAt — populated in BOTH the file-import path
    // (processFileContent) and the streaming-URL path (loadFromUrl onMeta callback).
    // The previous legacy `(this as any)._streamCreatedAt` was set only inside
    // processFileContent; after the Stage 2 streaming refactor, URL-loaded VODs
    // never went through processFileContent so the legacy field stayed stale, the
    // "now - maxOffset" fallback below kicked in for every load, and any
    // recent marker landed in the search window for ALL VODs (the user-reported
    // "метка появляется в каждом видео" bug).
    let streamStartMs = 0;
    const createdAtStr = this.state.streamCreatedAt();

    if (createdAtStr) {
      streamStartMs = new Date(createdAtStr).getTime();
    } else {
      // Fallback: use the current time minus max chat duration. This is rough
      // and lands the search window near "now" — should only be hit if videoInfo
      // failed to ship createdAt, which is rare for valid Twitch VODs.
      const bins = this.state.activityBins();
      const maxOffset = bins.length > 0 ? bins[bins.length - 1].startTime : 0;
      streamStartMs = new Date().getTime() - (maxOffset * 1000);
      console.warn('[Markers] No stream creation time on state, falling back to now-maxOffset. Marker matching may be incorrect.');
    }

    // Determine the search window's far edge. Historically this used ONLY the last
    // chat message's offset, but on a freshly-opened LIVE VOD the early auto-trigger
    // fetch (constructor effect) fires right after onMeta while chatComments is still
    // empty → offset 0 → window [streamStart, +1h] → every marker past the first hour
    // is missed until a live-poll tick (90s / 5min hidden) re-fetches with full data.
    // Mirror the user-clips window (fetchAndMergeUserClips): take the widest of the
    // last chat offset, the live duration, and the last analyzed bin so the window
    // always covers the VOD's real extent regardless of chat-download progress.
    // lastBinEnd is order-independent (bins key on floor(offset/15)) so it stays
    // correct even while parallel-downloaded comments arrive unsorted.
    const comments = this.state.chatComments();
    const lastOffsetSeconds = comments.length > 0
      ? (comments[comments.length - 1].content_offset_seconds || 0)
      : 0;
    const bins = this.state.activityBins();
    const lastBinEnd = bins.length > 0
      ? bins[bins.length - 1].startTime + this.BIN_DURATION_SECONDS
      : 0;
    const maxOffsetSeconds = Math.max(lastOffsetSeconds, this.state.liveDurationSec(), lastBinEnd);

    const startTime = new Date(streamStartMs);
    // Add 1 hour buffer (3600s) just in case the streamer pressed a marker slightly after the chat log ended
    const endTime = new Date(streamStartMs + (maxOffsetSeconds + 3600) * 1000);

    if (isDevMode()) {
      console.log(`[Markers] Stream Start: ${new Date(streamStartMs).toISOString()}`);
      console.log(`[Markers] Searching Firebase bounds: ${startTime.toISOString()} to ${endTime.toISOString()}`);
    }

    const rawMarkers = await this.firebaseService.fetchStreamMarkers(startTime, endTime);



    // Build sourceId → name lookup once for tag-prefix display in the description.
    const sourcesById = new Map(this.markersService.markerSources().map(s => [s.sourceId, s.name]));

    const markerHighlights: Highlight[] = rawMarkers
      .map((m) => {
        const markerTimeMs = new Date(m.timestamp).getTime();
        // Calculate offset in seconds
        const calculatedOffset = Math.floor((markerTimeMs - streamStartMs) / 1000);

        // If the marker was created before the stream started, it's invalid
        if (calculatedOffset < 0) return null;
        // If the marker was created days after, it's also probably not for this VOD
        // (unless the user is debugging with old VODs. For now, we'll allow it).

        const sourceName = m.sourceId ? (sourcesById.get(m.sourceId) || '—') : undefined;

        return <Highlight>{
          source: 'stream-marker',
          markerTag: m.tag || 'Метка',
          markerSourceId: m.sourceId,
          markerSourceName: sourceName,
          startTime: calculatedOffset,
          endTime: calculatedOffset + 5,
          messageCount: 0,
          sampleMessages: [],
          timestamp: this.formatTime(calculatedOffset),
          sentiment: 'Нейтральный',
          description: sourceName
            ? `[${sourceName}] ${m.tag || 'Момент'} (создано: ${m.time})`
            : `[МЕТКА] ${m.tag || 'Момент'} (Создано в: ${m.time})`,
          isAnalyzed: true,
          isPinned: false, // Don't auto-pin so they show in the main 'All' tab
          isDeleted: false,
          clipStartTime: Math.max(0, calculatedOffset - 15),
          clipEndTime: calculatedOffset + 30,
          estimatedSizeMb: this.calculateEstimatedSize(45),
          isStartTimeManuallySet: true
        };
      })
      .filter(m => m !== null) as Highlight[];

    this.state.streamMarkers.set(markerHighlights);
    this.reapplyStreamMarkers();
  }

  private reapplyStreamMarkers() {
    const fresh = this.state.streamMarkers();
    if (fresh.length === 0) return;

    this.state.highlights.update(current => {
      // Preserve user mutations (pin / delete / promote / border-edit) on existing
      // stream-markers across re-fetches. fetchAndMergeStreamMarkers rebuilds every
      // marker with isPinned/isDeleted=false defaults; without this carry-over each
      // re-fetch (auto-trigger on VOD open, every live-poll tick ~90s, next-session
      // restore) resurrected deleted markers back into «ВСЕ» and dropped pins /
      // promoted / custom clip borders. Same pattern as reapplyUserClips. Identity
      // key = markerSourceId + startTime: a given marker maps to a fixed offset for
      // a given VOD (streamStart and utcTime are both stable), and source-scoping
      // disambiguates same-second markers pressed from different paired copies.
      const keyOf = (h: Highlight) => `${h.markerSourceId ?? ''}:${h.startTime}`;
      const existingByKey = new Map<string, Highlight>(
        current.filter(h => h.source === 'stream-marker').map(h => [keyOf(h), h])
      );
      const merged: Highlight[] = fresh.map(m => {
        const prev = existingByKey.get(keyOf(m));
        if (!prev) return m;
        return {
          ...m,
          isPinned: prev.isPinned ?? false,
          isDeleted: prev.isDeleted ?? false,
          isPromoted: prev.isPromoted,
          clipStartTime: prev.clipStartTime ?? m.clipStartTime,
          clipEndTime: prev.clipEndTime ?? m.clipEndTime,
          isStartTimeManuallySet: prev.isStartTimeManuallySet ?? false,
        };
      });
      const filtered = current.filter(h => h.source !== 'stream-marker');
      return [...filtered, ...merged].sort((a, b) => a.startTime - b.startTime);
    });
  }

  // --- User-clips integration (GQL video.clips, Helix fallback → highlights[]) ---

  /**
   * Fetches user-made clips for the active VOD, maps each into a Highlight
   * (source='user-clip'), stores in state.userClips, and merges into highlights[]
   * via reapplyUserClips. Called by the auto-trigger effect, after chat download
   * (mirrors fetchAndMergeStreamMarkers placement) and on each live-poll tick so
   * fresh community clips surface as the stream progresses.
   *
   * Source: Twitch GQL `video(id).clips` (client-side, like chat), Helix
   * /get-vod-clips only as a fallback. Helix /clips has no video filter — it lists a
   * broadcaster's clips by created_at window, and that listing is lossy: the same query
   * twice returns different sets with duplicates across pages, and clips Helix itself
   * finds by id are missing from it. Measured 2026-10-20 on 3 VODs (xQc 14h, evelone,
   * buster): GQL ⊇ the union of every Helix query shape (single window, hourly
   * slices, daily slices after the stream), +14…40% clips; production's single window
   * also dropped every clip made from the VOD after the stream (evelone: 115).
   * videoOffsetSeconds === Helix vod_offset for all 653 shared clips.
   *
   * Returns false when nothing could be fetched (the auto-trigger retries once).
   */
  private async fetchAndMergeUserClips(): Promise<boolean> {
    const vodId = this.state.videoId();
    if (!vodId || checkDemoMode()) return false;
    const vod = String(vodId);
    const seq = ++this.userClipsFetchSeq;
    this.state.isLoadingClips.set(true);
    try {
      let clips: VodClip[] | null;
      try {
        clips = await gqlFetchVodClips(vod);
      } catch (e) {
        console.warn('[clips] GQL fetch failed, falling back to Helix:', e);
        clips = await this.fetchVodClipsViaHelix(vod);
      }
      if (!clips) return false;
      // VOD switched mid-fetch (the result belongs to another project), or a newer
      // fetch for this VOD already landed.
      if (String(this.state.videoId()) !== vod || seq < this.userClipsAppliedSeq) return true;
      this.userClipsAppliedSeq = seq;
      this.state.userClips.set(clips.map(c => this.mapClipToHighlight(c)));
      this.reapplyUserClips();
      return true;
    } catch (e) {
      console.warn('[clips] fetch failed:', e);
      return false;
    } finally {
      if (seq === this.userClipsFetchSeq) this.state.isLoadingClips.set(false);
    }
  }

  /** VOD categories → state.vodChapters. False on a failed request (the auto-trigger retries once). */
  private async fetchVodChapters(): Promise<boolean> {
    const vodId = this.state.videoId();
    if (!vodId || checkDemoMode()) return true;
    const vod = String(vodId);
    const seq = ++this.chaptersFetchSeq;
    try {
      const chapters = await gqlFetchVodChapters(vod);
      // VOD switched mid-fetch, or a newer fetch was started meanwhile.
      if (String(this.state.videoId()) !== vod || seq !== this.chaptersFetchSeq) return true;
      if (chapters) this.state.vodChapters.set({ videoId: vod, chapters });
      return true;
    } catch (e) {
      console.warn('[chapters] fetch failed:', e);
      return false;
    }
  }

  /**
   * Helix fallback (/get-vod-clips) — null when it can't be asked yet. broadcasterId
   * resolution order:
   *   1. state.broadcasterId — captured from /get-vod-status response
   *   2. the library project's channelId (= Twitch user id)
   *   3. libraryService.savedChannels lookup by streamer login/name
   */
  private async fetchVodClipsViaHelix(vodId: string): Promise<VodClip[] | null> {
    // /get-vod-clips requires a Firebase Bearer.
    if (!this.firebaseService.currentUser()) return null;
    let broadcasterId = this.state.broadcasterId();
    if (!broadcasterId) {
      broadcasterId = this.libraryService.getProject(vodId)?.channelId || null;
      const streamerName = this.state.streamerName();
      if (!broadcasterId && streamerName) {
        const target = streamerName.toLowerCase();
        const channel = this.libraryService.savedChannels().find(c =>
          c.login === target || c.name.toLowerCase() === target
        );
        broadcasterId = channel?.id || null;
      }
      if (broadcasterId) this.state.broadcasterId.set(broadcasterId);
    }

    const startedAt = this.state.streamCreatedAt();
    if (!broadcasterId || !startedAt) {
      console.warn('[clips] missing broadcasterId/startedAt — skipping Helix fallback');
      return null;
    }

    // The window must cover the whole VOD, so wait until its length is known: a live
    // VOD has liveDurationSec, a finished one a complete chat. Asking earlier (right
    // after onMeta, no bins yet) sent [start, start+2h] — on a 9h stream 19 of 112
    // clips — and the server caches the answer for 1h under a key without endedAt,
    // so the later full-window call got the short list back.
    // endedAt = max(liveDurationSec, last bin endTime, 1h floor) + 1h buffer. Twitch
    // /clips accepts future dates without complaint; over-broad date is harmless because
    // we post-filter by clip.video_id === vodId (backend) anyway.
    const startedAtMs = new Date(startedAt).getTime();
    const bins = this.state.activityBins();
    const lastBinEnd = bins.length > 0 ? bins[bins.length - 1].startTime + this.BIN_DURATION_SECONDS : 0;
    const liveDur = this.state.liveDurationSec();
    const lengthKnown = this.state.isLive()
      ? liveDur > 0
      : this.state.chatDownloadComplete() && lastBinEnd > 0;
    if (!lengthKnown) return null;
    const durationSec = Math.max(liveDur, lastBinEnd, 3600);
    const endedAtMs = startedAtMs + (durationSec + 3600) * 1000;
    const endedAt = new Date(endedAtMs).toISOString();

    const resp = await this.twitchApiService.getVodClips({
      broadcasterId: String(broadcasterId),
      vodId,
      startedAt,
      endedAt,
      isLive: this.state.isLive(),
    });
    return resp.clips;
  }

  private mapClipToHighlight(clip: VodClip): Highlight {
    // Twitch's `vod_offset` documents as "where the clip starts in the VOD" but
    // empirically it's the click moment — the clip "Save last 30s" UI captures
    // [click - duration, click], so the actual content START is vod_offset - duration.
    // Verified against multiple xQc clips: pin alignment matches content within
    // <1s when we subtract duration; without it, pin lands at the END of the clip.
    //
    // duration is fractional (e.g. 37.5) — ceil so our [start, end] window covers
    // the full clip and matches Twitch UI's rounded display ("38s" on the clip page).
    // Floor-ing here loses 1s on the START (= sub-1s less content captured).
    const dur = Math.max(1, Math.ceil(clip.duration));
    const start = Math.max(0, Math.floor(clip.vod_offset) - dur);
    const end = start + dur;
    return <Highlight>{
      source: 'user-clip',
      clipUrl: clip.url,
      clipCreatorName: clip.creator_name,
      clipViewCount: clip.view_count,
      // No clipThumbnailUrl: nothing renders it, and highlights[] rides in the cloud-synced
      // state doc (1MB cap) — ~180 B × hundreds of clips on a busy VOD.
      clipDurationSec: dur,
      timestamp: this.formatTime(start),
      sentiment: 'Сообщество',
      description: clip.title || '(без названия)',
      sampleMessages: [],
      messageCount: clip.view_count,
      startTime: start,
      endTime: end,
      clipStartTime: start,
      clipEndTime: end,
      isAnalyzed: true,
      isPinned: false,
      isDeleted: false,
      isStartTimeManuallySet: false,
      estimatedSizeMb: this.calculateEstimatedSize(dur),
    };
  }

  /**
   * Merges fresh state.userClips into highlights[] via mergeUserClips (utils/user-clips):
   * identity = clip slug, user mutations (pin/delete/promote/border-edit) carried over,
   * unique startTimes. Clips land long after the user may have selected something —
   * inserting them shifts indices, so the selection is re-found afterwards.
   */
  private reapplyUserClips(): void {
    const selected = this.state.currentHighlight();
    this.state.highlights.update(current => mergeUserClips(current, this.state.userClips()));
    if (!selected) return;
    const key = selected.source === 'user-clip' ? userClipKey(selected) : null;
    const idx = this.state.highlights().findIndex(h => key
      ? h.source === 'user-clip' && userClipKey(h) === key
      : h.startTime === selected.startTime);
    if (idx >= 0) this.state.currentHighlightIndex.set(idx);
  }

  private formatTime(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
}